import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ArrowClockwise, ArrowLeft, PencilSimple, Phone, Envelope, FileText, CreditCard, ChartBar, FilePdf, Wallet, Shield, Wrench, CalendarBlank, Clock, CurrencyEur, Plus, Receipt, Minus, CheckCircle, ToggleLeft, ToggleRight, CaretDown, CaretUp, Trash, Printer, DownloadSimple, Eye, EnvelopeSimple, NotePencil, Paperclip } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
import { printPdf, downloadPdf, openPdfInBrowser, getPdfBlobUrl } from '../utils/pdfUtils';
import DatePickerDMY from '../components/DatePickerDMY';
import SendEmailDialog from '../components/SendEmailDialog';
import InvoiceStatusBadge from '../components/InvoiceStatusBadge';
import { deriveInvoiceStatus } from '../components/InvoiceStatusBadge';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import AllocateCreditDialog from '../components/AllocateCreditDialog';
import CreditAllocationsDialog from '../components/CreditAllocationsDialog';
import OwnerHistoryTab from '../components/OwnerHistoryTab';
import { OwnerSelect } from '../components/OwnerSelect';
import { InvoicePaymentsDialog } from '../components/InvoicePaymentsDialog.jsx';
import { PaymentEmailHistoryDialog } from '../components/PaymentEmailHistoryDialog';
import ApartmentFormBody from '../components/ApartmentFormBody';

const formatDateDMY = (d) => { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };
const round2 = (n) => Math.round(n * 100) / 100;

const TABS = ['invoices', 'payments', 'creditNotes', 'statement', 'emailHistory', 'ownerHistory', 'notes'];

export default function ApartmentDetails() {
  const { apartmentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user, hasPerm } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Phase 1024e — Granular permission helpers. Admin always passes.
  // Staff requires the admin-granted "building_management.income_payments.*"
  // permission to see the action buttons for incoming payments / invoices
  // (an apartment invoice IS an income event, hence the same perm scope).
  const canPaymentCreate = isAdmin || hasPerm('building_management.income_payments.create');
  const canPaymentEdit   = isAdmin || hasPerm('building_management.income_payments.edit');
  const canPaymentDelete = isAdmin || hasPerm('building_management.income_payments.delete');
  const canInvoiceCreate = canPaymentCreate;
  const canInvoiceEdit   = canPaymentEdit;
  const canInvoiceDelete = canPaymentDelete;
  const canAptEdit       = isAdmin || hasPerm('building_management.apartments.edit');

  const [apartment, setApartment] = useState(null);
  const [activeTab, setActiveTab] = useState('invoices');

  // Email dialog
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailProps, setEmailProps] = useState({ pdfUrl: '', filename: '', defaultEmail: '', templateType: 'invoice', dynamicFields: {} });
  const openEmailDialog = (pdfUrl, filename, { templateType = 'invoice', dynamicFields = {}, email = '', relatedId = '' } = {}) => {
    setEmailProps({ pdfUrl, filename, defaultEmail: email, templateType, dynamicFields, relatedId });    setEmailDialogOpen(true);
  };
  const [payInvoice, setPayInvoice] = useState(null);
  const [emailHistPay, setEmailHistPay] = useState(null);
  // Receipt email: recipient defaults to the owner linked to the payment (editable in the dialog).
  const sendReceiptEmail = (p) => {
    const oh = (ownerHistoryList || []).find((o) => o.owner_history_id && o.owner_history_id === p.owner_history_id);
    const ownerName = oh ? `${oh.first_name || ''} ${oh.last_name || ''}`.trim() : (p.owner_name_snapshot || apartment.owner_name || '');
    openEmailDialog(`/api/apartment-payments/${p.payment_id}/pdf`, `Receipt_${p.receipt_number || apartment.apartment_number || ''}_${p.date || ''}.pdf`, {
      templateType: 'payment',
      dynamicFields: { apartment_number: apartment.apartment_number || '', owner_name: ownerName, building_name: apartment.building_name || '', receipt_number: p.receipt_number || '', amount: Math.abs(p.amount || 0).toFixed(2), payment_date: formatDateDMY(p.date) },
      email: (oh && oh.email) || apartment.email || '',
      relatedId: p.payment_id,
    });
  };
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [statement, setStatement] = useState(null);
  const [savedStatements, setSavedStatements] = useState([]);
  const [savedStmtOpen, setSavedStmtOpen] = useState(false);
  const [aptNotes, setAptNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryOpen, setEmailHistoryOpen] = useState(false);
  const [viewingEmail, setViewingEmail] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // Phase 645 — Tab-based Edit Apartment dialog.
  const [editTab, setEditTab] = useState('general');
  const [editForm, setEditForm] = useState({});
  // Phase 159 — Active Annual Expense Plan detection so Monthly Fee can be
  // rendered read-only with "FROM PLAN" lock, identical to Add Apartment.
  const [plansList, setPlansList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPeriod, setFilterPeriod] = useState('all');

  // Invoice date filter
  const [invFilterType, setInvFilterType] = useState('all');
  const [invDateFrom, setInvDateFrom] = useState('');
  const [invDateTo, setInvDateTo] = useState('');

  // Payment date filter
  const [payFilterType, setPayFilterType] = useState('all');
  const [payDateFrom, setPayDateFrom] = useState('');
  const [payDateTo, setPayDateTo] = useState('');

  // Payment dialog — managed by shared RecordPaymentDialog (Phase 276)
  const [payDialogOpen, setPayDialogOpen] = useState(false);

  // Edit Receipt state
  const [editReceiptDialogOpen, setEditReceiptDialogOpen] = useState(false);
  // Phase 302 — Allocate Credit dialog. Opens from the Opening Balance
  // card when the apartment has a negative OB with remaining credit.
  const [allocateCreditOpen, setAllocateCreditOpen] = useState(false);
  // Phase 1003 — View & edit existing OB credit allocations (audit + fix).
  const [viewAllocationsOpen, setViewAllocationsOpen] = useState(false);
  const [allocCreditInvoices, setAllocCreditInvoices] = useState([]);
  const [allocCreditAmounts, setAllocCreditAmounts] = useState({});
  const [allocCreditLoading, setAllocCreditLoading] = useState(false);
  const [deletePayDialogOpen, setDeletePayDialogOpen] = useState(false);
  const [deletingPay, setDeletingPay] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [editReceiptForm, setEditReceiptForm] = useState({ amount: '', payment_date: '', payment_method: 'cash', notes: '', receipt_number: '', owner_history_id: '' });
  const [editReceiptInvoices, setEditReceiptInvoices] = useState([]);
  const [editReceiptAllocations, setEditReceiptAllocations] = useState({});

  // Edit Payment state
  const [editPayDialogOpen, setEditPayDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPayForm, setEditPayForm] = useState({ amount: '', date: '', notes: '' });

  // Credit note dialog
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditForm, setCreditForm] = useState({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });

  // Credit notes tab
  const [creditNotes, setCreditNotes] = useState([]);
  const [cnFilterType, setCnFilterType] = useState('all');
  const [cnDateFrom, setCnDateFrom] = useState('');
  const [cnDateTo, setCnDateTo] = useState('');
  const [editCnDialogOpen, setEditCnDialogOpen] = useState(false);
  const [editingCn, setEditingCn] = useState(null);
  const [editCnForm, setEditCnForm] = useState({ amount: '', description: '', date: '' });

  // Auto invoicing
  const [buildingSettings, setBuildingSettings] = useState(null);

  // Credit allocation dialog
  const [creditAllocDialogOpen, setCreditAllocDialogOpen] = useState(false);
  const [creditAllocInvoices, setCreditAllocInvoices] = useState([]);
  const [creditAllocAmounts, setCreditAllocAmounts] = useState({});

  // Statement Opening Balance dialog
  const [stmtObDialogOpen, setStmtObDialogOpen] = useState(false);
  const [stmtObAmount, setStmtObAmount] = useState('');
  const [stmtObDate, setStmtObDate] = useState('');
  const [stmtDateFrom, setStmtDateFrom] = useState('');
  const [stmtDateTo, setStmtDateTo] = useState('');
  // Phase 702 — Owner selector. When set, the Statement (and its PDF /
  // email) shows ONLY transactions that belong to this owner so previous
  // owners never appear in the current owner's accounting history.
  const [stmtOwnerHistoryId, setStmtOwnerHistoryId] = useState('');
  const [ownerHistoryList, setOwnerHistoryList] = useState([]);

  // Overview period filter
  const [ovFilterType, setOvFilterType] = useState('all');
  const [ovMonth, setOvMonth] = useState('');
  const [ovYear, setOvYear] = useState('');
  const [ovDateFrom, setOvDateFrom] = useState('');
  const [ovDateTo, setOvDateTo] = useState('');

  // Invoice CRUD
  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  // Phase 1002r — Live preview of the effective Reserve Fund that will be
  // auto-added to a Common Expenses invoice on Save. Refreshed whenever
  // the dialog's period changes.
  const [effectiveReserve, setEffectiveReserve] = useState(null);
  const [invForm, setInvForm] = useState({ invoice_number: '', invoice_date: '', period: '', amount: '', notes: '', use_period: false,
    // Phase 735.61 — multi-month allocation state
    start_period: '', end_period: '', allocation_mode: 'equal', manual_allocations: [],
    // Phase 985 — Immutable owner attribution. `owner_history_id` is
    // stamped onto the invoice at creation time so future ownership
    // changes never rewrite historical invoices. Empty string is
    // allowed (legacy invoices with no explicit owner).
    owner_history_id: '' });
  const [deleteInvDialogOpen, setDeleteInvDialogOpen] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);

  // Recurring rules
  const [recurringRules, setRecurringRules] = useState([]);
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({ day_of_month: 1, amount: '', start_date: new Date().toISOString().split('T')[0], end_date: '', send_email: false, scope: 'apartment', recipient_email: '' });

  // Phase 1013 — Build the "Send invoice to" dropdown options from the
  // apartment record. Mirrors the backend `_apartment_email_recipients`
  // list (owner, tenant, contact, extras) so the admin can pick which
  // address will receive the recurring invoice.
  const recipientOptionsForApartment = () => {
    if (!apartment) return [];
    const out = [];
    const seen = new Set();
    const push = (email, label, name) => {
      const e = (email || '').trim();
      if (e && !seen.has(e)) {
        out.push({ email: e, label, name: (name || '').trim() });
        seen.add(e);
      }
    };
    push(apartment.email, 'Owner', apartment.owner_name);
    push(apartment.tenant_email, 'Tenant', apartment.tenant_name);
    push(apartment.contact_email, 'Contact', apartment.contact_name);
    (apartment.extra_emails || []).forEach((e, i) => push(e, `Extra ${i + 1}`, ''));
    return out;
  };

  // Compute available periods and filtered payments
  const availablePeriods = [...new Set(payments.filter(p => p.period_month && p.period_year).map(p => `${t(`month_${p.period_month}`)} ${p.period_year}`))].sort().reverse();
  const filteredPayments = filterPeriod === 'all' ? payments : payments.filter(p => {
    if (!p.period_month || !p.period_year) return false;
    return `${t(`month_${p.period_month}`)} ${p.period_year}` === filterPeriod;
  });

  // Date range helper
  const getDateRange = (type) => {
    const now = new Date();
    if (type === 'thisMonth') {
      const from = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const to = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      return { from, to };
    }
    if (type === 'lastMonth') {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
      const to = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];
      return { from, to };
    }
    if (type === 'thisYear') {
      const from = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
      const to = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
      return { from, to };
    }
    return { from: '', to: '' };
  };

  // Apply quick filter for invoices
  const applyInvQuickFilter = (type) => {
    setInvFilterType(type);
    if (type === 'all') { setInvDateFrom(''); setInvDateTo(''); }
    else if (type !== 'custom') { const r = getDateRange(type); setInvDateFrom(r.from); setInvDateTo(r.to); }
  };

  // Apply quick filter for payments
  const applyPayQuickFilter = (type) => {
    setPayFilterType(type);
    if (type === 'all') { setPayDateFrom(''); setPayDateTo(''); }
    else if (type !== 'custom') { const r = getDateRange(type); setPayDateFrom(r.from); setPayDateTo(r.to); }
  };

  // Filtered invoices
  // Apartment charge categories only (exclude building expenses like electricity, elevator, cleaning)
  const APARTMENT_CATEGORIES = new Set(['management', 'common_expenses', 'reserve_fund', 'repair', 'extra_reserve']);

  const filteredInvoices = invoices
    .filter(inv => {
      // Only show apartment-relevant categories
      const cat = inv.category || inv.invoice_type || '';
      if (cat && !APARTMENT_CATEGORIES.has(cat)) return false;
      if (!invDateFrom && !invDateTo) return true;
      // Phase 1003c — Corrupt-date resilience. When `invoice_date` /
      // `period` carries a malformed year (< 1900), the record must
      // still surface in the Invoices tab so the user can inspect and
      // edit it. Fall back to `created_at` for filtering only; the
      // stored value is preserved and rendered as-is on the row so
      // the malformation stays visible for correction. (Real DB case:
      // INV-279 apt 301 with `period="0202-12"`.)
      const rawPeriod = inv.period || '';
      const rawInvDate = inv.invoice_date || '';
      const yearFromPeriod = parseInt(rawPeriod.slice(0, 4), 10);
      const yearFromInvDate = parseInt(rawInvDate.slice(0, 4), 10);
      const periodValid = yearFromPeriod >= 1900 && yearFromPeriod <= 2100;
      const invDateValid = yearFromInvDate >= 1900 && yearFromInvDate <= 2100;
      const invDate = (invDateValid ? rawInvDate : '')
        || (periodValid ? rawPeriod : '')
        || (inv.created_at || '').slice(0, 10);
      if (invDateFrom && invDate < invDateFrom) return false;
      if (invDateTo && invDate > invDateTo) return false;
      return true;
    })
    // Phase 248 — newest invoice first. Primary key is invoice_date; we
    // fall back to period (YYYY-MM) and finally created_at so older rows
    // without an explicit invoice_date still sort correctly.
    // Phase 1003c — Malformed years (< 1900) fall back to created_at
    // for sort key too, otherwise the invoice would sink to the very
    // bottom of the newest-first list and be invisible in practice.
    .sort((a, b) => {
      const sortKey = (inv) => {
        const rawIvD = inv.invoice_date || '';
        const rawPer = inv.period || '';
        const yIvD = parseInt(rawIvD.slice(0, 4), 10);
        const yPer = parseInt(rawPer.slice(0, 4), 10);
        if (yIvD >= 1900 && yIvD <= 2100) return rawIvD;
        if (yPer >= 1900 && yPer <= 2100) return `${rawPer}-01`;
        return (inv.created_at || '').slice(0, 10);
      };
      const ka = sortKey(a);
      const kb = sortKey(b);
      if (kb !== ka) return kb.localeCompare(ka);
      // tie-breaker: created_at desc
      return (b.created_at || '').localeCompare(a.created_at || '');
    });

  // Filtered payments (override old filterPeriod approach)
  const dateFilteredPayments = filteredPayments.filter(p => {
    if (!payDateFrom && !payDateTo) return true;
    const pDate = p.date || p.payment_date || '';
    if (payDateFrom && pDate < payDateFrom) return false;
    if (payDateTo && pDate > payDateTo) return false;
    return true;
  });

  // Apply quick filter for credit notes
  const applyCnQuickFilter = (type) => {
    setCnFilterType(type);
    if (type === 'all') { setCnDateFrom(''); setCnDateTo(''); }
    else if (type !== 'custom') { const r = getDateRange(type); setCnDateFrom(r.from); setCnDateTo(r.to); }
  };

  // Filtered credit notes
  const filteredCreditNotes = creditNotes.filter(cn => {
    if (!cnDateFrom && !cnDateTo) return true;
    const cnDate = (cn.created_at || cn.period || '').slice(0, 10);
    if (cnDateFrom && cnDate < cnDateFrom) return false;
    if (cnDateTo && cnDate > cnDateTo) return false;
    return true;
  });

  // Overview period filter helpers
  const applyOvQuickFilter = (type) => {
    setOvFilterType(type);
    setOvMonth('');
    setOvYear('');
    if (type === 'all') { setOvDateFrom(''); setOvDateTo(''); }
    else if (type === 'custom') { /* keep custom dates */ }
    else { const r = getDateRange(type); setOvDateFrom(r.from); setOvDateTo(r.to); }
  };

  const applyOvMonthYear = (month, year) => {
    setOvMonth(month);
    setOvYear(year);
    if (month && year) {
      const m = parseInt(month);
      const y = parseInt(year);
      const from = `${y}-${String(m).padStart(2, '0')}-01`;
      const to = new Date(y, m, 0).toISOString().split('T')[0];
      setOvDateFrom(from);
      setOvDateTo(to);
      setOvFilterType('monthYear');
    } else if (year && !month) {
      setOvDateFrom(`${year}-01-01`);
      setOvDateTo(`${year}-12-31`);
      setOvFilterType('monthYear');
    } else {
      setOvDateFrom('');
      setOvDateTo('');
      setOvFilterType('all');
    }
  };

  // Available years from invoices
  const ovAvailableYears = [...new Set(invoices.map(inv => (inv.period || '').substring(0, 4)).filter(y => y && y !== ''))].sort().reverse();

  // Filtered overview invoices
  const ovFilteredInvoices = invoices.filter(inv => {
    if (!ovDateFrom && !ovDateTo) return true;
    const invDate = inv.period ? `${inv.period}-01` : (inv.created_at || '').slice(0, 10);
    if (ovDateFrom && invDate < ovDateFrom) return false;
    if (ovDateTo && invDate > ovDateTo) return false;
    return true;
  });

  // Filtered overview payments
  const ovFilteredPayments = payments.filter(p => {
    if (!ovDateFrom && !ovDateTo) return true;
    const pDate = p.date || p.payment_date || '';
    if (ovDateFrom && pDate < ovDateFrom) return false;
    if (ovDateTo && pDate > ovDateTo) return false;
    return true;
  });

  // Computed overview totals
  const isOvFiltered = ovFilterType !== 'all';
  const ovTotalCharges = isOvFiltered
    ? ovFilteredInvoices.filter(i => i.status !== 'credit_note').reduce((s, i) => s + Math.abs(i.amount || 0), 0)
    : (apartment?.total_due || 0);
  const ovTotalPaid = isOvFiltered
    ? ovFilteredPayments.reduce((s, p) => s + Math.abs(p.amount || 0), 0)
    : (apartment?.total_paid || 0);
  const ovBalance = isOvFiltered
    ? round2(ovTotalCharges - ovTotalPaid)
    : (apartment?.remaining_balance || 0);
  const ovInvoiceCount = isOvFiltered ? ovFilteredInvoices.filter(i => i.status !== 'credit_note').length : (apartment?.invoice_count || 0);
  const ovPaymentCount = isOvFiltered ? ovFilteredPayments.length : (apartment?.payment_count || 0);


  const fetchAll = async () => {
    try {
      const [aptRes, invRes, payRes, cnRes] = await Promise.all([
        axios.get(`${API}/apartments/${apartmentId}`),
        axios.get(`${API}/apartments/${apartmentId}/invoices`),
        axios.get(`${API}/apartments/${apartmentId}/payments`),
        axios.get(`${API}/apartments/${apartmentId}/credit-notes`),
      ]);
      setApartment(aptRes.data);
      setInvoices(invRes.data || []);
      setPayments(payRes.data || []);
      setCreditNotes(cnRes.data || []);
      // Fetch statement with date + owner filters
      const stmtParams = new URLSearchParams();
      if (stmtDateFrom) stmtParams.set('date_from', stmtDateFrom);
      if (stmtDateTo) stmtParams.set('date_to', stmtDateTo);
      if (stmtOwnerHistoryId) stmtParams.set('owner_history_id', stmtOwnerHistoryId);
      const stmtUrl = `${API}/apartments/${apartmentId}/statement${stmtParams.toString() ? '?' + stmtParams.toString() : ''}`;
      const stmtRes = await axios.get(stmtUrl);
      setStatement(stmtRes.data || null);
      // Fetch building auto-invoicing setting
      if (aptRes.data?.building_id) {
        try {
          const bldRes = await axios.get(`${API}/buildings/${aptRes.data.building_id}`);
          setBuildingSettings(bldRes.data || null);
        } catch { /* ignore */ }
        // Phase 159 — fetch Annual Expense Plans so the Edit modal can detect
        // an Active plan and lock the Monthly Fee input.
        try {
          const plansRes = await axios.get(`${API}/buildings/${aptRes.data.building_id}/annual-expenses/plans?year=${new Date().getFullYear()}`);
          setPlansList(plansRes.data?.plans || []);
        } catch { /* ignore */ }
      }
      // Fetch recurring rules for this apartment and building
      try {
        const rulesRes = await axios.get(`${API}/recurring-rules?building_id=${aptRes.data?.building_id || ''}`);
        const allRules = rulesRes.data || [];
        const relevant = allRules.filter(r => r.scope === 'building' || r.apartment_id === apartmentId);
        setRecurringRules(relevant);
      } catch { /* ignore */ }
      // Fetch saved statements
      try {
        const ssRes = await axios.get(`${API}/apartments/${apartmentId}/saved-statements`);
        setSavedStatements(ssRes.data || []);
      } catch { /* ignore */ }
      // Fetch apartment notes
      try {
        const notesRes = await axios.get(`${API}/apartments/${apartmentId}/notes`);
        setAptNotes(notesRes.data || []);
      } catch { /* ignore */ }
      // Fetch email history
      try {
        const ehRes = await axios.get(`${API}/email-logs?apartment_id=${apartmentId}`);
        setEmailHistory(ehRes.data || []);
      } catch { /* ignore */ }
      // Phase 702 — Fetch the full owner history list so the Statement
      // tab can render an Owner dropdown EVEN WHEN one owner is
      // selected (in that case the statement response only contains
      // the selected period).
      try {
        const ohRes = await axios.get(`${API}/apartments/${apartmentId}/owner-history`);
        setOwnerHistoryList(ohRes.data || []);
      } catch { /* ignore */ }
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [apartmentId, stmtDateFrom, stmtDateTo, stmtOwnerHistoryId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 282 — honor ?tab= query param so deep-links (e.g. from the
  // building-wide Owner History timeline) jump straight to the right tab.
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (urlTab && TABS.includes(urlTab)) {
      setActiveTab(urlTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('tab')]);

  // Auto-open the Edit Receipt dialog if ?edit_receipt=<RCP-NNNN> is on the URL.
  // This lets the building-level Payments tab link directly into the full
  // reallocation UI without duplicating the dialog code.
  useEffect(() => {
    const rcp = searchParams.get('edit_receipt');
    if (!rcp) return;
    openEditReceipt(rcp).finally(() => {
      // Remove the param so refreshing doesn't re-open
      const p = new URLSearchParams(searchParams);
      p.delete('edit_receipt');
      setSearchParams(p, { replace: true });
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('edit_receipt')]);

  // Phase 1002r — Refresh the effective Reserve Fund preview whenever the
  // invoice dialog is open and the target Period changes. Skipped for
  // Edit-mode (historical invoices must never be silently mutated) and
  // when the dialog is closed.
  useEffect(() => {
    if (!invDialogOpen || editingInvoice) { setEffectiveReserve(null); return; }
    const period = (invForm.use_period ? (invForm.period || invForm.start_period) : (invForm.invoice_date || '').slice(0, 7));
    if (!period || period.length !== 7 || !apartment?.building_id || !apartmentId) {
      setEffectiveReserve(null); return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ period, apartment_id: apartmentId });
        const res = await axios.get(
          `${API}/buildings/${apartment.building_id}/extra-reserve-fund/effective?${params.toString()}`,
        );
        if (!cancelled) setEffectiveReserve(res.data);
      } catch { if (!cancelled) setEffectiveReserve(null); }
    })();
    return () => { cancelled = true; };
  }, [invDialogOpen, editingInvoice, invForm.period, invForm.start_period, invForm.use_period, invForm.invoice_date, apartment?.building_id, apartmentId]);


  const openEditDialog = () => {
    setEditForm({
      apartment_number: apartment?.apartment_number || '',
      owner_name: apartment?.owner_name || '',
      phone: apartment?.phone || '',
      email: apartment?.email || '',
      address: apartment?.address || '',
      // Phase 208 — Tenant + payment responsibility + multi-email support
      tenant_name: apartment?.tenant_name || '',
      tenant_email: apartment?.tenant_email || '',
      tenant_phone: apartment?.tenant_phone || '',
      payment_responsibility: apartment?.payment_responsibility || 'owner',
      extra_emails: apartment?.extra_emails || [],
      primary_email: apartment?.primary_email || '',
      notes: apartment?.notes || '',
      monthly_fee: apartment?.monthly_fee || 0,
      common_expenses: apartment?.common_expenses || 0,
      reserve_fund: apartment?.reserve_fund || 0,
      repairs_fund: apartment?.repairs_fund || 0,
      share_percentage: apartment?.share_percentage || 0,
      opening_balance: apartment?.opening_balance || '',
      opening_balance_date: apartment?.opening_balance_date || '',
      // Phase 242 — Bound strictly to this apartment_id; never copied
      // from siblings or buildings on rename.
      opening_balance_notes: apartment?.opening_balance_notes || '',
      // Phase 385 — Contact Person + Email Recipients parity with Add form
      contact_name: apartment?.contact_name || '',
      contact_phone: apartment?.contact_phone || '',
      contact_email: apartment?.contact_email || '',
      email_recipients: apartment?.email_recipients || { owner: true, contact: false, extras: [] },
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/apartments/${apartmentId}`, {
        ...editForm,
        monthly_fee: parseFloat(editForm.monthly_fee) || 0,
        common_expenses: parseFloat(editForm.common_expenses) || 0,
        reserve_fund: parseFloat(editForm.reserve_fund) || 0,
        repairs_fund: parseFloat(editForm.repairs_fund) || 0,
        share_percentage: parseFloat(editForm.share_percentage) || 0,
        opening_balance: parseFloat(editForm.opening_balance) || 0,
        opening_balance_date: editForm.opening_balance_date || '',
        opening_balance_notes: editForm.opening_balance_notes || '',
        // Phase 385 — Persist Contact Person + Email Recipients (parity with Add form)
        contact_name: editForm.contact_name || '',
        contact_phone: editForm.contact_phone || '',
        contact_email: editForm.contact_email || '',
        email_recipients: editForm.email_recipients || { owner: true, contact: false, extras: [] },
      });
      toast.success(t('savedSuccess'));
      setEditDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  if (loading) return <Layout><div className="p-12 text-center text-zinc-400">Loading...</div></Layout>;
  if (!apartment) return <Layout><div className="p-12 text-center text-zinc-400">Apartment not found</div></Layout>;

  const totalCharges = statement?.total_charges || 0;
  const totalPayments = statement?.total_payments || 0;
  // Phase 303 (rev2) — Use the canonical balance everywhere (negative=debt,
  // positive=credit), aligned with the statement's `closing_balance`.
  // This is the SAME value the sidebar uses, so sidebar / header /
  // Invoices tab / Statement card / PDF all agree.
  const finalBalance = (statement?.canonical_balance !== undefined && statement?.canonical_balance !== null)
    ? statement.canonical_balance
    : (statement?.closing_balance || 0);
  const openingBalance = statement?.opening_balance || 0;

  const stmtPdfParams = () => {
    const p = {};
    if (stmtDateFrom) p.date_from = stmtDateFrom;
    if (stmtDateTo) p.date_to = stmtDateTo;
    // Phase 702 — Propagate owner selection to PDF / email so the
    // generated file matches what the user is looking at on screen.
    if (stmtOwnerHistoryId) p.owner_history_id = stmtOwnerHistoryId;
    return p;
  };

  const handlePrintStatementPdf = () => {
    printPdf(`/apartments/${apartmentId}/statement/pdf`, stmtPdfParams());
  };

  const handleDownloadStatementPdf = () => {
    downloadPdf(`/apartments/${apartmentId}/statement/pdf`, stmtPdfParams());
    toast.success(t('pdfExported'));
  };

  const handleEmailStatement = () => {
    const params = stmtPdfParams();
    const qs = Object.entries(params).map(([k, v]) => `${k}=${v}`).join('&');
    const pdfPath = `/api/apartments/${apartmentId}/statement/pdf${qs ? '?' + qs : ''}`;
    // Use filter dates, fallback to statement data range
    const effectiveFrom = stmtDateFrom || (statement?.entries?.length ? statement.entries[0]?.date : '') || '';
    const effectiveTo = stmtDateTo || new Date().toISOString().split('T')[0];
    const dateLabel = effectiveFrom && effectiveTo
      ? `${formatDateDMY(effectiveFrom)}-${formatDateDMY(effectiveTo)}`
      : effectiveFrom ? `from_${formatDateDMY(effectiveFrom)}` : 'full';
    openEmailDialog(
      pdfPath,
      `Statement_${apartment?.apartment_number || ''}_${dateLabel}.pdf`,
      {
        templateType: 'statement',
        dynamicFields: {
          apartment_number: apartment?.apartment_number || '',
          owner_name: apartment?.owner_name || '',
          building_name: apartment?.building_name || '',
          start_date: effectiveFrom ? formatDateDMY(effectiveFrom) : '',
          end_date: effectiveTo ? formatDateDMY(effectiveTo) : '',
        },
        // Phase 208 — Default to the responsible payer (owner OR tenant) and
        // fall back to whichever email is available on the apartment.
        email: (apartment?.payment_responsibility === 'tenant' ? apartment?.tenant_email : apartment?.email)
          || apartment?.email
          || apartment?.tenant_email
          || '',
      }
    );
  };

  const handleSaveStatement = async () => {
    try {
      await axios.post(`${API}/apartments/${apartmentId}/saved-statements`, {
        date_from: stmtDateFrom || '',
        date_to: stmtDateTo || '',
        owner_history_id: stmtOwnerHistoryId || '',
        total_charges: statement?.total_charges || 0,
        total_payments: statement?.total_payments || 0,
        closing_balance: statement?.closing_balance || 0,
      });
      toast.success(t('statementSaved'));
      const ssRes = await axios.get(`${API}/apartments/${apartmentId}/saved-statements`);
      setSavedStatements(ssRes.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDeleteSavedStatement = async (stmtId) => {
    try {
      await axios.delete(`${API}/apartments/${apartmentId}/saved-statements/${stmtId}`);
      setSavedStatements(prev => prev.filter(s => s.statement_id !== stmtId));
      toast.success(t('deleted'));
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNoteText.trim()) return;
    try {
      const res = await axios.post(`${API}/apartments/${apartmentId}/notes`, { text: newNoteText.trim() });
      setAptNotes(prev => [res.data, ...prev]);
      setNewNoteText('');
      toast.success(t('noteSaved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteText.trim()) return;
    try {
      await axios.put(`${API}/apartments/${apartmentId}/notes/${noteId}`, { text: editNoteText.trim() });
      setAptNotes(prev => prev.map(n => n.note_id === noteId ? { ...n, text: editNoteText.trim(), updated_at: new Date().toISOString() } : n));
      setEditingNoteId(null);
      toast.success(t('savedSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/apartments/${apartmentId}/notes/${noteId}`);
      setAptNotes(prev => prev.filter(n => n.note_id !== noteId));
      toast.success(t('deleted'));
    } catch {
      toast.error(t('errorOccurred'));
    }
  };

  const fetchEmailHistory = async () => {
    try {
      const ehRes = await axios.get(`${API}/email-logs?apartment_id=${apartmentId}`);
      setEmailHistory(ehRes.data || []);
    } catch { /* ignore */ }
  };

  // ---- Payment Flow ----
  // Phase 276 — Recording payments is handled entirely by the shared
  // RecordPaymentDialog component, which uses the same POST /receipts
  // endpoint. We only retain the open/close state here and refresh data
  // via `fetchAll` after a successful save.
  const openPaymentDialog = () => setPayDialogOpen(true);

  // ---- Edit Receipt ----
  const openEditReceipt = async (receiptNumber, fallbackPaymentId = null) => {
    try {
      // Find receipt by receipt_number
      const rcpRes = await axios.get(`${API}/receipts?apartment_id=${apartmentId}`);
      const rcp = (rcpRes.data || []).find(r => r.receipt_number === receiptNumber);
      if (!rcp) {
        // Phase 247 — Defensive fallback: when the underlying receipt
        // document is missing (orphan apartment_payment), gracefully open
        // the payment PDF instead of throwing "Receipt not found". This
        // keeps audit access usable even when data drift has occurred.
        if (fallbackPaymentId) {
          openPdfInBrowser(`/apartment-payments/${fallbackPaymentId}/pdf`);
          return;
        }
        toast.error(t('receiptNotFound') || 'Receipt not found');
        return;
      }
      setEditingReceipt(rcp);
      setEditReceiptForm({
        amount: rcp.amount || '',
        payment_date: rcp.payment_date || '',
        payment_method: rcp.payment_method || 'cash',
        notes: rcp.notes || '',
        receipt_number: rcp.receipt_number || '',
        owner_history_id: rcp.owner_history_id || '',
      });
      // Load invoices for re-allocation.
      // Phase 298 — Show ONLY: invoices already allocated in THIS receipt
      // (so the user can edit/remove them) + currently unpaid/partially-paid
      // invoices (so new allocations are possible). Already-paid unrelated
      // invoices are hidden to avoid clutter.
      const invRes = await axios.get(`${API}/apartments/${apartmentId}/invoices`);
      const allocatedIds = new Set((rcp.invoice_details || [])
        .map(d => (d.invoice_id || '').trim())
        .filter(id => id && id !== 'OPENING'));
      // Phase 735.59 — Build a map of this receipt's OWN prior allocation
      // per invoice so we can back it out of `paid_amount` before showing
      // `remaining` in the dialog. Without this, invoices this same
      // receipt already paid appear with `remaining = 0` and the HTML
      // <input max={balance}> field refuses any keystroke ("Value must be
      // 0") — even when the user only wanted to change the Payment
      // Method (Bank ↔ Cash) and keep the same allocation.
      const priorByInv = {};
      for (const d of (rcp.invoice_details || [])) {
        const iid = (d.invoice_id || '').trim();
        if (!iid || iid === 'OPENING') continue;
        priorByInv[iid] = (priorByInv[iid] || 0) + Number(d.paid_in_this_receipt || 0);
      }
      const allInv = (invRes.data || [])
        .filter(inv => inv.status !== 'credit_note')
        .filter(inv => {
          if (allocatedIds.has(inv.invoice_id)) return true;
          const remaining = (inv.amount || 0) - (inv.paid_amount || 0);
          return remaining > 0.01;
        })
        // Attach `paid_amount_effective` (paid MINUS this receipt's own
        // contribution) and `remaining` (invoice amount − effective paid)
        // so the row reflects the balance the user is truly editing.
        .map(inv => {
          const priorHere = Number(priorByInv[inv.invoice_id] || 0);
          const effectivePaid = Math.max(0, Number(inv.paid_amount || 0) - priorHere);
          return {
            ...inv,
            paid_amount: effectivePaid,
            remaining: Math.max(0, Number(inv.amount || 0) - effectivePaid),
          };
        })
        // Phase 735.57 — sort by invoice_date oldest first, then invoice_number
        // so the professional allocation table (and Auto Allocate) always
        // consumes the oldest receivable first (FIFO).
        .sort((a, b) => {
          const da = (a.invoice_date || a.date || a.period || '');
          const db = (b.invoice_date || b.date || b.period || '');
          if (da !== db) return da.localeCompare(db);
          return (a.invoice_number || '').localeCompare(b.invoice_number || '');
        });
      // Phase 243 — Prepend a virtual OPENING row when the apartment carries
      // an unpaid opening balance debt. We pull the apartment doc to compute
      // remaining = opening_balance - opening_balance_paid.
      let openingRow = null;
      try {
        const aptRes = await axios.get(`${API}/apartments/${apartmentId}`);
        const a = aptRes.data || {};
        const ob = parseFloat(a.opening_balance || 0);
        const obPaid = parseFloat(a.opening_balance_paid || 0);
        // Pre-existing OPENING allocation in this very receipt should ADD
        // back to the available remaining (since editing reverses it).
        const priorOpening = (rcp.invoice_details || []).find(d => d.invoice_id === 'OPENING');
        const priorPaid = priorOpening ? parseFloat(priorOpening.paid_in_this_receipt || 0) : 0;
        const remaining = Math.max(ob - obPaid + priorPaid, 0);
        if (ob > 0 && remaining > 0.01) {
          openingRow = {
            invoice_id: 'OPENING',
            is_opening_balance: true,
            period: 'OPENING',
            invoice_number: '',
            amount: ob,
            paid_amount: obPaid,
            remaining,
            opening_balance_notes: a.opening_balance_notes || '',
            status: obPaid > 0.01 ? 'partially_paid' : 'unpaid',
          };
        }
      } catch (_) { /* leave openingRow null */ }
      const combined = openingRow ? [openingRow, ...allInv] : allInv;
      setEditReceiptInvoices(combined);
      // Pre-fill allocations from receipt's invoice_details (preserves OPENING).
      const allocs = {};
      (rcp.invoice_details || []).forEach(d => { allocs[d.invoice_id] = { amount: d.paid_in_this_receipt || 0 }; });
      setEditReceiptAllocations(allocs);
      setEditReceiptDialogOpen(true);
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const editReceiptTotalAllocated = Object.values(editReceiptAllocations).reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const editReceiptAmount = parseFloat(editReceiptForm.amount) || 0;

  const handleEditReceiptSubmit = async (e) => {
    e.preventDefault();
    // Phase 700 — Allow 0.00 edits so the user can void a payment without
    // deleting it (allocations cascade to 0 via canonical recompute).
    if (!editingReceipt || editReceiptAmount < 0) return;
    try {
      const selections = Object.entries(editReceiptAllocations)
        .filter(([, a]) => (parseFloat(a.amount) || 0) > 0)
        .map(([id, a]) => ({ invoice_id: id, amount: parseFloat(a.amount) || 0 }));

      const origSel = (editingReceipt.invoice_details || []).filter((d) => Number(d.paid_in_this_receipt || 0) > 0)
        .map((d) => `${d.invoice_id}:${Number(d.paid_in_this_receipt).toFixed(2)}`).sort().join('|');
      const newSel = selections.map((s) => `${s.invoice_id}:${Number(s.amount).toFixed(2)}`).sort().join('|');
      const onlyOwner = Number(editReceiptAmount).toFixed(2) === Number(editingReceipt.amount || 0).toFixed(2)
        && (editReceiptForm.payment_date || '') === (editingReceipt.payment_date || '')
        && (editReceiptForm.payment_method || '') === (editingReceipt.payment_method || '')
        && (editReceiptForm.notes || '') === (editingReceipt.notes || '')
        && (editReceiptForm.receipt_number || '').trim() === (editingReceipt.receipt_number || '')
        && origSel === newSel;
      if (onlyOwner && (editReceiptForm.owner_history_id || '') !== (editingReceipt.owner_history_id || '')) {
        // Owner re-attribution only: no re-posting of amounts / allocations / fund ledger.
        await axios.put(`${API}/receipts/${editingReceipt.receipt_id}`, { owner_history_id: editReceiptForm.owner_history_id || '' });
      } else await axios.put(`${API}/receipts/${editingReceipt.receipt_id}`, {
        amount: editReceiptAmount,
        payment_date: editReceiptForm.payment_date,
        payment_method: editReceiptForm.payment_method,
        notes: editReceiptForm.notes,
        // Phase 241 — Allow editing the receipt number. Empty value keeps
        // the existing one; non-empty triggers a uniqueness check on the
        // backend and cascades to apartment_payments.
        receipt_number: (editReceiptForm.receipt_number || '').trim() || undefined,
        // Phase 735.58 — Always send `invoice_selections` (even when empty)
        // so the backend can distinguish an explicit "clear all" from
        // "no change requested". Previously we sent `null` on empty,
        // which the backend interpreted as "keep old ratios" — meaning
        // Clear Allocation appeared to work in the UI but never
        // persisted, leaving allocations in a state the user thought
        // they had removed.
        invoice_selections: selections,
        ...(ownerHistoryList.length > 0 ? { owner_history_id: editReceiptForm.owner_history_id || '' } : {}),
      });
      toast.success(t('savedSuccess'));
      setEditReceiptDialogOpen(false);
      setEditingReceipt(null);
      fetchAll();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(t('receiptNumberAlreadyExists') || 'Receipt number already exists.');
      } else {
        toast.error(error.response?.data?.detail || t('errorOccurred'));
      }
    }
  };

  // ---- Credit Note ----
  const openCreditDialog = () => {
    setCreditForm({ amount: '', description: '', date: new Date().toISOString().split('T')[0] });
    setCreditDialogOpen(true);
  };

  const handleCreditSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(creditForm.amount);
    if (!amount || amount <= 0) { toast.error(t('errorOccurred')); return; }
    try {
      await axios.post(`${API}/apartments/${apartmentId}/credit-note`, {
        amount,
        description: creditForm.description,
        date: creditForm.date,
      });
      toast.success(t('creditNoteCreated'));
      setCreditDialogOpen(false);
      fetchAll();
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  // ---- Edit Payment ----
  // Payments tied to a receipt (the normal case) are edited through the full
  // Edit-Receipt dialog, which carries the invoice allocation UI (list of open
  // invoices, per-invoice amount inputs, live allocation summary). This is the
  // same flow triggered by clicking the RCP-XXXX link in the Payments table.
  // Only legacy/credit-applied rows that have no receipt_number fall back to
  // the simple amount/date/notes edit dialog.
  const openEditPayment = (p) => {
    if (p?.receipt_number) {
      // Phase 247 — Pass payment_id as fallback; if the underlying receipt
      // doc is missing, we open the PDF instead of failing.
      openEditReceipt(p.receipt_number, p.payment_id);
      return;
    }
    setEditingPayment(p);
    setEditPayForm({ amount: Math.abs(p.amount || 0), date: p.date || '', notes: p.notes || '', owner_history_id: p.owner_history_id || '' });
    setEditPayDialogOpen(true);
  };

  const handleEditPayment = async () => {
    if (!editingPayment) return;
    try {
      await axios.put(`${API}/apartment-payments/${editingPayment.payment_id}`, editPayForm);
      toast.success(t('paymentUpdated'));
      setEditPayDialogOpen(false);
      setEditingPayment(null);
      // Phase 1010 — broadcast so Apartments list in BuildingDetails refreshes
      try {
        window.dispatchEvent(new CustomEvent('cleanpro:apartment-payment-updated', {
          detail: { building_id: apartment?.building_id, apartment_id: apartmentId, saved_at: new Date().toISOString() },
        }));
      } catch { /* non-fatal */ }
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // ---- Apply Credit (Manual) ----
  const openCreditAllocDialog = async () => {
    try {
      const invRes = await axios.get(`${API}/apartments/${apartmentId}/invoices`);
      const unpaid = (invRes.data || []).filter(inv => inv.status !== 'paid' && inv.status !== 'credit_note' && (inv.remaining || 0) > 0);
      unpaid.sort((a, b) => (a.period || '').localeCompare(b.period || ''));
      setCreditAllocInvoices(unpaid);
      setCreditAllocAmounts({});
      setCreditAllocDialogOpen(true);
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  const creditAllocTotal = Object.values(creditAllocAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const availableCredit = apartment?.credit_balance || 0;

  const handleApplyCreditManual = async (e) => {
    e.preventDefault();
    if (creditAllocTotal <= 0 || creditAllocTotal > availableCredit + 0.01) {
      toast.error(t('allocationMismatch'));
      return;
    }
    try {
      const allocations = Object.entries(creditAllocAmounts)
        .filter(([, amt]) => (parseFloat(amt) || 0) > 0)
        .map(([id, amt]) => ({ invoice_id: id, amount: parseFloat(amt) }));
      const res = await axios.post(`${API}/apartments/${apartmentId}/apply-credit-manual`, { allocations });
      toast.success(`${t('creditApplied')}: €${res.data.applied.toFixed(2)}`);
      setCreditAllocDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // ---- Statement Opening Balance ----
  const openStmtObDialog = () => {
    setStmtObAmount(apartment?.opening_balance != null ? String(apartment.opening_balance) : '0');
    setStmtObDate(apartment?.opening_balance_date || '');
    setStmtObDialogOpen(true);
  };

  const handleSaveStmtOb = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/apartments/${apartmentId}`, {
        opening_balance: parseFloat(stmtObAmount) || 0,
        opening_balance_date: stmtObDate,
      });
      toast.success(t('savedSuccess'));
      setStmtObDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // ---- Manual Invoice CRUD ----
  // Phase 985 — Smart owner attribution helpers. `owner_history_id` is
  // stamped onto every new/edited invoice so historical invoices stay
  // pinned to their original owner even if the apartment's current owner
  // is later replaced. The dropdown lists ALL historical owners (any
  // count) and the auto-suggestion picks the owner whose ownership
  // period overlaps the invoice Start Period (YYYY-MM).
  const _getOwnerHistoryIdForPeriod = (periodYYYYMM) => {
    if (!ownerHistoryList || ownerHistoryList.length === 0) return '';
    if (!periodYYYYMM || !/^\d{4}-\d{2}$/.test(periodYYYYMM)) return '';
    // Anchor: last day of the given month. An owner matches when
    // start_date <= end-of-month AND (end_date empty OR end_date >= start-of-month).
    const [yy, mm] = periodYYYYMM.split('-').map(Number);
    const monthStart = `${yy}-${String(mm).padStart(2, '0')}-01`;
    const lastDay = new Date(yy, mm, 0).getDate();
    const monthEnd = `${yy}-${String(mm).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    const candidates = ownerHistoryList.filter(oh => {
      const s = (oh.start_date || '').slice(0, 10);
      const e = (oh.end_date || '').slice(0, 10);
      // Missing start_date → treat as "always started". Missing end_date → still active.
      if (s && s > monthEnd) return false;
      if (e && e < monthStart) return false;
      return true;
    });
    if (candidates.length === 0) return '';
    // Prefer the candidate with the latest start_date (most specific).
    candidates.sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
    return candidates[0].owner_history_id;
  };
  const _getCurrentOwnerHistoryId = () => {
    if (!ownerHistoryList || ownerHistoryList.length === 0) return '';
    // Prefer active status, then no end_date, then latest start_date.
    const active = ownerHistoryList.find(oh => oh.status === 'active');
    if (active) return active.owner_history_id;
    const open = ownerHistoryList.find(oh => !oh.end_date);
    if (open) return open.owner_history_id;
    const sorted = [...ownerHistoryList].sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
    return sorted[0]?.owner_history_id || '';
  };
  const _ownerNameSnapshot = (ohId) => {
    if (!ohId) return '';
    const oh = (ownerHistoryList || []).find(x => x.owner_history_id === ohId);
    if (!oh) return '';
    return [oh.first_name, oh.last_name].filter(Boolean).join(' ').trim() || (oh.owner_name || '');
  };

  const openCreateInvoice = () => {
    setEditingInvoice(null);
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    const periodStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    // Phase 985 — default owner: prefer the one that matches TODAY's
    // period; fall back to the current/active owner if no match.
    const suggestedOh = _getOwnerHistoryIdForPeriod(periodStr) || _getCurrentOwnerHistoryId();
    setInvForm({
      invoice_number: '',
      invoice_date: todayStr,
      period: periodStr,
      amount: apartment?.monthly_fee || '',
      notes: '',
      // Phase 802 — Default to Use Period ON so the Period / Start /
      // End fields auto-fill from Invoice Date without an extra toggle.
      // Users can still uncheck if they want an invoice with no period.
      use_period: true,
      start_period: periodStr,
      end_period: periodStr,
      allocation_mode: 'equal',
      manual_allocations: [],
      owner_history_id: suggestedOh,
    });
    setInvDialogOpen(true);
  };
  const openEditInvoice = (inv) => {
    setEditingInvoice(inv);
    const initialPeriod = inv.start_period || inv.period || '';
    // Phase 985 — Load the invoice's stamped owner_history_id if present;
    // otherwise try to reconstruct it from the invoice period. NEVER
    // overwrite existing invoices silently — the auto-derive only kicks in
    // when the DB has no explicit value (legacy rows).
    const existingOh = (inv.owner_history_id || '').trim();
    const resolvedOh = existingOh || _getOwnerHistoryIdForPeriod(initialPeriod) || '';
    setInvForm({
      invoice_number: inv.invoice_number || '',
      invoice_date: inv.invoice_date || (inv.period ? `${inv.period}-01` : ''),
      period: inv.period || '',
      amount: inv.amount,
      notes: inv.notes || '',
      use_period: !!inv.period,
      start_period: inv.start_period || inv.period || '',
      end_period: inv.end_period || inv.period || '',
      allocation_mode: inv.allocation_mode || 'equal',
      manual_allocations: Array.isArray(inv.period_allocations) ? inv.period_allocations : [],
      owner_history_id: resolvedOh,
    });
    setInvDialogOpen(true);
  };
  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const amt = parseFloat(invForm.amount) || 0;
      // Phase 735.61 — validate the range + manual sum before sending.
      if (invForm.use_period) {
        if (invForm.end_period && invForm.start_period && invForm.end_period < invForm.start_period) {
          toast.error('End Period cannot be before Start Period.'); return;
        }
        if (invForm.allocation_mode === 'manual') {
          const sum = (invForm.manual_allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
          if (Math.abs(sum - amt) > 0.01) {
            toast.error(`Manual allocations sum to €${sum.toFixed(2)} but invoice total is €${amt.toFixed(2)}.`);
            return;
          }
        }
      }
      const payload = {
        invoice_number: (invForm.invoice_number || '').trim(),
        invoice_date: invForm.invoice_date || '',
        period: invForm.use_period ? (invForm.period || invForm.start_period || '') : '',
        amount: amt,
        notes: invForm.notes || '',
        // Phase 985 — Immutable owner attribution. Send even when empty so
        // the caller can explicitly clear it. The backend takes an
        // immutable name snapshot to preserve historical accuracy.
        owner_history_id: (invForm.owner_history_id || '').trim(),
        owner_name_snapshot: _ownerNameSnapshot(invForm.owner_history_id),
      };
      // Phase 735.61 — attach multi-month fields when Use Period is ON.
      if (invForm.use_period) {
        payload.start_period = invForm.start_period || invForm.period || '';
        payload.end_period = invForm.end_period || payload.start_period;
        payload.allocation_mode = invForm.allocation_mode || 'equal';
        if (payload.allocation_mode === 'manual') {
          payload.period_allocations = (invForm.manual_allocations || []).map(a => ({
            year: Number(a.year), month: Number(a.month), amount: Number(a.amount) || 0,
          }));
        }
      }
      if (editingInvoice) {
        await axios.put(`${API}/apartments/${apartmentId}/invoices/${editingInvoice.invoice_id}`, payload);
      } else {
        await axios.post(`${API}/apartments/${apartmentId}/invoices`, payload);
      }
      toast.success(t('savedSuccess'));
      setInvDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };
  const handleDeleteInvoice = async () => {
    if (!deletingInvoice) return;
    try {
      await axios.delete(`${API}/apartments/${apartmentId}/invoices/${deletingInvoice.invoice_id}`);
      toast.success(t('deleteSuccess'));
      setDeleteInvDialogOpen(false);
      setDeletingInvoice(null);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // ---- Recurring Rules ----
  const openCreateRule = () => {
    setEditingRule(null);
    // Phase 1013 — When creating a new rule and only one email exists on
    // the apartment, auto-select it as the recipient. If none exist, we
    // leave it blank and the form will surface a warning.
    const opts = recipientOptionsForApartment();
    const defaultRecipient = opts.length === 1 ? opts[0].email : '';
    setRuleForm({ day_of_month: 1, amount: apartment?.monthly_fee || '', start_date: new Date().toISOString().split('T')[0], end_date: '', send_email: opts.length > 0, scope: 'apartment', recipient_email: defaultRecipient });
    setRuleDialogOpen(true);
  };
  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({ day_of_month: rule.day_of_month, amount: rule.amount || '', start_date: rule.start_date, end_date: rule.end_date || '', send_email: rule.send_email || false, scope: rule.scope, recipient_email: rule.recipient_email || '' });
    setRuleDialogOpen(true);
  };
  const handleRuleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        building_id: apartment.building_id,
        apartment_id: ruleForm.scope === 'apartment' ? apartmentId : undefined,
        day_of_month: parseInt(ruleForm.day_of_month) || 1,
        amount: ruleForm.amount ? parseFloat(ruleForm.amount) : null,
        start_date: ruleForm.start_date,
        end_date: ruleForm.end_date || null,
        // Phase 1013 — `auto_email` is the admin-facing key; backend
        // maps it to `send_email` internally for back-compat.
        auto_email: !!ruleForm.send_email,
        recipient_email: (ruleForm.recipient_email || '').trim(),
      };
      if (editingRule) {
        await axios.put(`${API}/recurring-rules/${editingRule.rule_id}`, payload);
      } else {
        await axios.post(`${API}/recurring-rules`, payload);
      }
      toast.success(t('savedSuccess'));
      setRuleDialogOpen(false);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };
  const toggleRuleStatus = async (rule) => {
    try {
      await axios.put(`${API}/recurring-rules/${rule.rule_id}`, { status: rule.status === 'active' ? 'paused' : 'active' });
      fetchAll();
    } catch (err) { toast.error(t('errorOccurred')); }
  };
  const handleDeleteRule = async (rule) => {
    try {
      await axios.delete(`${API}/recurring-rules/${rule.rule_id}`);
      toast.success(t('deleteSuccess'));
      fetchAll();
    } catch (err) { toast.error(t('errorOccurred')); }
  };
  const handleRunRecurring = async () => {
    try {
      const res = await axios.post(`${API}/recurring-rules/run`);
      toast.success(`${res.data.message}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || t('errorOccurred')); }
  };

  // ---- Auto Invoicing Toggle ----
  const toggleAutoInvoicing = async () => {
    if (!apartment?.building_id) return;
    try {
      const newVal = !(buildingSettings?.auto_invoicing);
      await axios.put(`${API}/buildings/${apartment.building_id}/auto-invoicing`, { enabled: newVal });
      setBuildingSettings(prev => ({ ...prev, auto_invoicing: newVal }));
      toast.success(t('savedSuccess'));
    } catch (error) { toast.error(error.response?.data?.detail || t('errorOccurred')); }
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="apartment-details-page">
        {/* Header — Customer Info always visible */}
        <div className="flex items-start gap-4">
          <button onClick={() => navigate(`/buildings/${apartment.building_id}?tab=apartments`)} className="p-2 hover:bg-zinc-100 mt-1" data-testid="back-to-building">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-3">
              <h1 className="text-xl font-bold" style={{ fontFamily: 'var(--font-heading)' }} data-testid="apt-title">
                #{apartment.apartment_number} — {apartment.owner_name || t('noOwner')}
              </h1>
            </div>
            <p className="text-sm text-zinc-500 mt-0.5">{apartment.building_name}</p>
            {/* Phase 208 — Tenant + payment responsibility inline summary */}
            {(apartment.tenant_name || apartment.payment_responsibility === 'tenant') && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs">
                {apartment.tenant_name && (
                  <span className="flex items-center gap-1 text-zinc-500">
                    Tenant: <span className="font-medium text-zinc-700">{apartment.tenant_name}</span>
                    {apartment.tenant_phone && <span className="font-mono text-zinc-400 ml-1">({apartment.tenant_phone})</span>}
                  </span>
                )}
                <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border font-semibold ${
                  apartment.payment_responsibility === 'tenant'
                    ? 'bg-amber-50 text-amber-700 border-amber-300'
                    : 'bg-blue-50 text-blue-700 border-blue-200'
                }`}>
                  Pays: {apartment.payment_responsibility === 'tenant' ? 'Tenant' : 'Owner'}
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-xs text-zinc-500">
              {apartment.phone && (
                <span className="flex items-center gap-1"><Phone size={12} className="text-zinc-400" /><span className="font-mono">{apartment.phone}</span></span>
              )}
              {apartment.email && (
                <span className="flex items-center gap-1"><Envelope size={12} className="text-zinc-400" /><span className="font-mono">{apartment.email}</span></span>
              )}
              <span className="flex items-center gap-1 text-zinc-400">{t('monthlyFee')}: <span className="font-mono font-medium text-zinc-600">&euro;{(apartment.monthly_fee || 0).toFixed(2)}</span></span>
              <span className="flex items-center gap-1 text-zinc-400">{t('sharePercentage')}: <span className="font-mono font-medium text-zinc-600">{apartment.share_percentage || 0}%</span></span>
            </div>
          </div>
          {canAptEdit && (
            <Button variant="outline" onClick={openEditDialog} className="rounded-none flex-shrink-0" data-testid="edit-apt-btn">
              <PencilSimple size={14} className="mr-1.5" />{t('edit')}
            </Button>
          )}
        </div>

        {/* Tabs with badges */}
        <div className="border-b border-zinc-200 flex gap-0 overflow-x-auto" data-testid="apt-tabs">
          {TABS.map(tab => {
            let badge = null;
            if (tab === 'invoices') {
              const unpaid = invoices.filter(i => i.status !== 'paid' && i.status !== 'credit_note').length;
              if (unpaid > 0) badge = unpaid;
            } else if (tab === 'payments') {
              badge = payments.length || null;
            } else if (tab === 'creditNotes') {
              const cnCount = invoices.filter(i => i.status === 'credit_note').length;
              if (cnCount > 0) badge = cnCount;
            } else if (tab === 'notes') {
              if (aptNotes.length > 0) badge = aptNotes.length;
            }
            return (
              <button key={tab} onClick={() => setActiveTab(tab)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeTab === tab ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-zinc-300'}`}
                data-testid={`apt-tab-${tab}`}
              >
                {t(tab)}
                {badge && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-zinc-200 text-zinc-600 rounded-sm">{badge}</span>}
              </button>
            );
          })}
        </div>

        {/* ====== INVOICES TAB ====== */}
        {activeTab === 'invoices' && (
          <div className="space-y-6" data-testid="apt-invoices-tab">
            {/* Recurring Invoices Section */}
            {isAdmin && (
              <div className="bg-white border border-zinc-200 p-5 space-y-4" data-testid="recurring-rules-section">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-bold text-zinc-800">{t('recurringInvoices')}</h4>
                    <p className="text-xs text-zinc-400 mt-0.5">{t('recurringInvoicesDesc')}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleRunRecurring} variant="outline" size="sm" className="rounded-none text-xs" data-testid="run-recurring-btn">
                      <Clock size={14} className="mr-1" />{t('generateNow')}
                    </Button>
                    <Button onClick={openCreateRule} size="sm" className="bg-zinc-950 rounded-none text-xs" data-testid="add-rule-btn">
                      <Plus size={14} className="mr-1" />{t('addRule')}
                    </Button>
                  </div>
                </div>
                {recurringRules.length === 0 ? (
                  <p className="text-xs text-zinc-400 py-3">{t('noRecurringRules')}</p>
                ) : (
                  <div className="space-y-2">
                    {recurringRules.map(rule => {
                      // Phase 1013 — Email status label + colour derived
                      // from the persisted history on the rule.
                      const status = rule.last_email_status;
                      const statusLabel = ({
                        sent: t('emailStatusSent'),
                        failed: t('emailStatusFailed'),
                        no_recipient: t('emailStatusNoRecipient'),
                        skipped_testing: t('emailStatusSkippedTesting'),
                        mocked: t('emailStatusMocked'),
                      }[status]) || (status || t('emailStatusPending'));
                      const statusCls = status === 'sent' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : status === 'failed' ? 'bg-red-50 text-red-700 border-red-200'
                        : status === 'no_recipient' ? 'bg-amber-50 text-amber-800 border-amber-200'
                        : 'bg-zinc-50 text-zinc-600 border-zinc-200';
                      return (
                      <div key={rule.rule_id} className={`px-4 py-3 border ${rule.status === 'active' ? 'border-emerald-200 bg-emerald-50/30' : 'border-zinc-200 bg-zinc-50/50'}`} data-testid={`rule-${rule.rule_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <button onClick={() => toggleRuleStatus(rule)} className="flex-shrink-0" data-testid={`toggle-rule-${rule.rule_id}`}>
                              {rule.status === 'active' ? <ToggleRight size={24} weight="fill" className="text-emerald-600" /> : <ToggleLeft size={24} weight="fill" className="text-zinc-400" />}
                            </button>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold">{t('monthly')} - {t('dayOfMonth')}: {rule.day_of_month}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 font-semibold uppercase ${rule.scope === 'building' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-purple-50 text-purple-700 border border-purple-200'}`}>
                                  {rule.scope === 'building' ? t('allApartments') : t('thisApartment')}
                                </span>
                                {rule.send_email && <span className="text-[10px] px-1.5 py-0.5 bg-cyan-50 text-cyan-700 border border-cyan-200 font-semibold">{t('emailEnabled')}</span>}
                              </div>
                              <div className="text-xs text-zinc-500 mt-0.5">
                                {rule.amount ? `€${rule.amount.toFixed(2)}` : t('useMonthlyFee')} &middot; {t('from')}: {formatDateDMY(rule.start_date)}
                                {rule.end_date ? ` → ${formatDateDMY(rule.end_date)}` : ` → ${t('noEndDate')}`}
                                {rule.next_invoice_date && <span className="ml-2 text-emerald-600 font-medium">{t('nextScheduled')}: {formatDateDMY(rule.next_invoice_date)}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => openEditRule(rule)} className="p-1.5 hover:bg-zinc-100" data-testid={`edit-rule-${rule.rule_id}`}><PencilSimple size={14} /></button>
                            <button onClick={() => handleDeleteRule(rule)} className="p-1.5 hover:bg-red-50 text-red-500" data-testid={`delete-rule-${rule.rule_id}`}><Minus size={14} /></button>
                          </div>
                        </div>
                        {/* Phase 1013 — History block. Only shown when at
                            least one of the tracked fields is populated. */}
                        {(rule.last_invoice_id || rule.last_email_sent_at || rule.last_generated || rule.send_email) && (
                          <div className="mt-3 pt-3 border-t border-zinc-200/60 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]" data-testid={`rule-history-${rule.rule_id}`}>
                            <div>
                              <p className="uppercase tracking-wider text-zinc-400">{t('lastInvoiceGenerated')}</p>
                              <p className="text-zinc-700 font-mono mt-0.5">{rule.last_generated || t('never')}</p>
                            </div>
                            <div>
                              <p className="uppercase tracking-wider text-zinc-400">{t('lastEmailSent')}</p>
                              <p className="text-zinc-700 font-mono mt-0.5">{rule.last_email_sent_at ? formatDateDMY(rule.last_email_sent_at.slice(0, 10)) : t('never')}</p>
                            </div>
                            <div>
                              <p className="uppercase tracking-wider text-zinc-400">{t('recipientEmail')}</p>
                              <p className="text-zinc-700 font-mono mt-0.5 truncate" title={rule.last_email_recipient || rule.recipient_email || ''}>{rule.last_email_recipient || rule.recipient_email || '—'}</p>
                            </div>
                            <div>
                              <p className="uppercase tracking-wider text-zinc-400">{t('emailStatus')}</p>
                              <span className={`inline-block mt-0.5 px-1.5 py-0.5 border font-semibold ${statusCls}`} data-testid={`rule-status-${rule.rule_id}`}>{statusLabel}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );})}
                  </div>
                )}
              </div>
            )}

            {/* Invoice List with CRUD */}
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-zinc-800">{t('invoices')}</h4>
              <div className="flex gap-2">
                {isAdmin && availableCredit > 0 && (
                  <Button onClick={openCreditAllocDialog} size="sm" variant="outline" className="rounded-none text-xs" data-testid="apply-credit-invoices-btn">
                    <CurrencyEur size={14} className="mr-1" />{t('applyCredit')} (&euro;{availableCredit.toFixed(2)})
                  </Button>
                )}
                {canInvoiceCreate && (
                  <Button onClick={openCreateInvoice} size="sm" className="bg-zinc-950 rounded-none text-xs" data-testid="create-invoice-btn">
                    <Plus size={14} className="mr-1" />{t('createInvoice')}
                  </Button>
                )}
              </div>
            </div>

            {/* Phase 295/302 — Opening Balance card. Always visible in the
                invoices tab regardless of value (zero, positive, or negative)
                for full audit transparency. A NEGATIVE OB represents
                available CREDIT and never auto-marks as paid: its
                "Remaining" stays negative until the user explicitly
                allocates the credit to specific unpaid invoices via the
                "Allocate Credit" action. */}
            {(() => {
              const ob = parseFloat(apartment?.opening_balance) || 0;
              const obPaid = parseFloat(apartment?.opening_balance_paid) || 0;
              const sign = ob > 0 ? 1 : ob < 0 ? -1 : 0;
              const obRemaining = sign * Math.max(0, Math.abs(ob) - obPaid);
              const isDebt = obRemaining > 0.005;
              const isCredit = obRemaining < -0.005;
              const cls = isDebt ? 'text-red-600' : 'text-zinc-700';
              const signLabel = ob > 0 ? '+' : ob < 0 ? '-' : '';
              const remSignLabel = obRemaining > 0 ? '+' : obRemaining < 0 ? '-' : '';
              return (
                <div
                  className="bg-white border border-zinc-200 px-5 py-3 flex items-center justify-between hover:bg-zinc-50/60 cursor-pointer transition-colors"
                  data-testid="inv-opening-balance-card"
                  onClick={(e) => {
                    // Ignore clicks that originated from the inline buttons
                    // (they have their own onClick handlers).
                    if (e.target.closest('button')) return;
                    if (ob !== 0) setViewAllocationsOpen(true);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('openingBalance')}</p>
                    {apartment?.opening_balance_date && (
                      <p className="text-[11px] text-zinc-400 font-mono mt-0.5">{formatDateDMY(apartment.opening_balance_date)}</p>
                    )}
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      {t('clickToManageAllocations') || 'Click to view / manage allocations'}
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400">{t('amount')}</p>
                      <p className="text-base font-mono font-semibold text-zinc-700 mt-0.5" data-testid="inv-ob-amount">
                        {signLabel}&euro;{Math.abs(ob).toFixed(2)}
                      </p>
                    </div>
                    {obPaid > 0 && (
                      <div className="text-right">
                        <p className="text-[10px] uppercase tracking-wider text-zinc-400">{t('paid')}</p>
                        <p className="text-base font-mono text-zinc-700 mt-0.5">&euro;{obPaid.toFixed(2)}</p>
                      </div>
                    )}
                    <div className="text-right">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400">{t('remaining')}</p>
                      <p className={`text-base font-mono font-semibold mt-0.5 ${cls}`} data-testid="inv-ob-remaining">
                        {remSignLabel}&euro;{Math.abs(obRemaining).toFixed(2)}
                      </p>
                    </div>
                    {isCredit && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-none"
                        onClick={(e) => { e.stopPropagation(); setViewAllocationsOpen(true); }}
                        data-testid="view-allocations-btn"
                      >
                        {t('manageAllocations') || 'Manage Allocations'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Invoice Date Filter */}
            <div className="flex flex-col sm:flex-row flex-wrap items-end gap-3 bg-white border border-zinc-200 p-3 mb-3" data-testid="inv-date-filter">
              <div>
                <Label className="text-xs text-zinc-500">{t('period')}</Label>
                <Select value={invFilterType} onValueChange={(val) => applyInvQuickFilter(val)}>
                  <SelectTrigger className="w-[180px] rounded-none h-9 text-sm mt-1" data-testid="inv-filter-select">
                    <SelectValue placeholder={t('allTime')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allTime')}</SelectItem>
                    <SelectItem value="thisMonth">{t('thisMonth')}</SelectItem>
                    <SelectItem value="lastMonth">{t('lastMonth')}</SelectItem>
                    <SelectItem value="thisYear">{t('thisYear')}</SelectItem>
                    <SelectItem value="custom">{t('customRange')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(invFilterType === 'custom' || invDateFrom || invDateTo) && (
                <>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('dateFrom')}</Label>
                    <DatePickerDMY value={invDateFrom} onChange={v => { setInvDateFrom(v); setInvFilterType('custom'); }} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="inv-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('dateTo')}</Label>
                    <DatePickerDMY value={invDateTo} onChange={v => { setInvDateTo(v); setInvFilterType('custom'); }} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="inv-date-to" />
                  </div>
                  <Button type="button" variant="outline" onClick={() => applyInvQuickFilter('all')} className="rounded-none h-9 text-xs" data-testid="inv-clear-filter">{t('clearFilter')}</Button>
                </>
              )}
              {invDateFrom && <span className="text-xs text-zinc-400 self-center ml-auto">{filteredInvoices.length} / {invoices.length}</span>}
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
            {filteredInvoices.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">{t('noInvoices')}</div>
            ) : (
              /* Phase 283 — Apartment invoices table redesign.
                 - All rows are single-line (whitespace-nowrap, fixed col widths).
                 - Neutral palette only; RED is reserved for remaining>0 / unpaid.
                 - Status is plain text (no badges, no colored backgrounds).
                 - Numbers right-aligned, type/date/period centered, tabular-nums. */
              <table className="apt-invoices-table">
                <colgroup>
                  <col style={{ width: '110px' }} />{/* Invoice #     */}
                  <col style={{ width: '100px' }} />{/* Date          */}
                  <col style={{ width: '80px'  }} />{/* Period        */}
                  <col style={{ width: '120px' }} />{/* Type          */}
                  <col style={{ width: '100px' }} />{/* Amount        */}
                  <col style={{ width: '100px' }} />{/* Paid          */}
                  <col style={{ width: '110px' }} />{/* Remaining     */}
                  <col style={{ width: '150px' }} />{/* Status        */}
                  <col />                            {/* Notes (flex) */}
                  <col style={{ width: '150px' }} />{/* Actions column — always shown (buttons gated individually) */}
                </colgroup>
                <thead>
                  <tr>
                    <th className="text-left">{t('invoice')}</th>
                    <th className="text-center">{t('invoiceDate') || 'Invoice Date'}</th>
                    <th className="text-center">{t('period')}</th>
                    <th className="text-center">{t('type')}</th>
                    <th className="text-right">{t('amount')}</th>
                    <th className="text-right">{t('paidAmount')}</th>
                    <th className="text-right">{t('remaining')}</th>
                    <th className="text-left">{t('status')}</th>
                    <th className="text-left">{t('notes')}</th>
                    <th className="text-center">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map(inv => {
                    const amount = Math.abs(inv.amount || 0);
                    const paid = Math.max(0, inv.paid_amount || 0);
                    const remaining = Math.max(0, inv.remaining || 0);
                    const status = deriveInvoiceStatus(inv.amount, inv.paid_amount);
                    const isUnpaid = status === 'unpaid';
                    const hasRemaining = remaining > 0;
                    const statusLabel = status === 'paid'
                      ? t('paid')
                      : status === 'partial'
                      ? (t('partialPayment') || t('partial'))
                      : t('unpaid');
                    const typeLabel = (inv.invoice_type === 'repair' || inv.category === 'repair')
                      ? t('repair')
                      : (inv.invoice_type === 'reserve_fund' || inv.category === 'reserve_fund' || inv.invoice_type === 'extra_reserve' || inv.category === 'extra_reserve')
                      ? t('reserveFund')
                      : t('commonExpenses');
                    return (
                      <tr key={inv.invoice_id} data-testid={`apt-inv-${inv.invoice_id}`}>
                        <td className="text-left font-mono font-semibold text-zinc-800" data-testid={`inv-number-${inv.invoice_id}`}>
                          {inv.invoice_number || '—'}
                        </td>
                        <td className="text-center font-mono text-zinc-700" data-testid={`inv-date-${inv.invoice_id}`}>
                          {formatDateDMY(inv.invoice_date) || '—'}
                        </td>
                        <td className="text-center font-mono text-zinc-700" data-testid={`inv-period-${inv.invoice_id}`}>
                          {(() => {
                            // Phase 735.61 — Show "YYYY-MM → YYYY-MM" when
                            // the invoice spans multiple months (Start ≠ End).
                            const sp = inv.start_period || inv.period || '';
                            const ep = inv.end_period || inv.period || '';
                            if (sp && ep && sp !== ep) return `${sp} → ${ep}`;
                            return inv.period || sp || '—';
                          })()}
                        </td>
                        <td className="text-center text-[11px] uppercase tracking-wider text-zinc-600">{typeLabel}</td>
                        <td className="text-right font-mono text-zinc-800">€{amount.toFixed(2)}</td>
                        <td className="text-right font-mono text-zinc-700">€{paid.toFixed(2)}</td>
                        <td className={`text-right font-mono font-semibold ${hasRemaining ? 'text-red-600' : 'text-zinc-400'}`}>
                          €{remaining.toFixed(2)}
                        </td>
                        <td
                          className={`text-left text-[12px] font-medium uppercase tracking-wider ${isUnpaid ? 'text-red-600' : 'text-zinc-600'}`}
                          data-testid={`apt-inv-status-${inv.invoice_id}`}
                        >
                          {statusLabel}
                        </td>
                        <td className="text-left text-xs text-zinc-500 truncate" title={inv.notes || ''}>
                          {inv.notes || (inv.auto_generated ? t('autoGenerated') : '—')}
                        </td>
                        <td className="text-center">
                            <div className="inline-flex items-center gap-0.5">
                              {(Number(inv.paid_amount || 0) > 0 || ['paid', 'partially_paid'].includes(inv.status)) && (
                                <button onClick={() => setPayInvoice(inv)} className="p-1 hover:bg-emerald-50 text-emerald-600 hover:text-emerald-800 transition-colors" title={language === 'el' ? 'Προβολή πληρωμών / αποδείξεων' : 'View payments / receipts'} data-testid={`view-inv-payments-${inv.invoice_id}`}><Receipt size={14} /></button>
                              )}
                              <button onClick={() => openPdfInBrowser(`/apartments/${apartmentId}/invoices/${inv.invoice_id}/pdf`)} className="p-1 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors" title={t('view')} data-testid={`view-inv-${inv.invoice_id}`}><Eye size={14} /></button>
                              <button onClick={() => printPdf(`/apartments/${apartmentId}/invoices/${inv.invoice_id}/pdf`)} className="p-1 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors" title={t('printPdf')} data-testid={`print-inv-${inv.invoice_id}`}><Printer size={14} /></button>
                              <button onClick={() => openEmailDialog(
                                `/api/apartments/${apartmentId}/invoices/${inv.invoice_id}/pdf`,
                                `Invoice_${inv.invoice_number || inv.invoice_id}.pdf`,
                                {
                                  templateType: 'invoice',
                                  dynamicFields: {
                                    apartment_number: apartment.apartment_number || '',
                                    invoice_number: inv.invoice_number || inv.invoice_id || '',
                                    owner_name: apartment.owner_name || '',
                                    building_name: apartment.building_name || '',
                                    month: inv.period || '',
                                  },
                                  email: apartment.email || '',
                                }
                              )} className="p-1 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors" title={t('sendByEmail')} data-testid={`email-inv-${inv.invoice_id}`}><EnvelopeSimple size={14} /></button>
                              {canInvoiceEdit && (
                                <button onClick={() => openEditInvoice(inv)} className="p-1 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors" title={t('edit')} data-testid={`edit-inv-${inv.invoice_id}`}><PencilSimple size={14} /></button>
                              )}
                              {canInvoiceDelete && (
                                <button onClick={() => { setDeletingInvoice(inv); setDeleteInvDialogOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-500 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-inv-${inv.invoice_id}`}><Trash size={14} /></button>
                              )}
                            </div>
                          </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="text-right text-zinc-500 uppercase tracking-wider text-[11px]">{t('total')}</td>
                    <td className="text-right font-mono font-semibold text-zinc-800">€{filteredInvoices.reduce((s, i) => s + Math.abs(i.amount || 0), 0).toFixed(2)}</td>
                    <td className="text-right font-mono font-semibold text-zinc-800">€{filteredInvoices.reduce((s, i) => s + Math.max(0, i.paid_amount || 0), 0).toFixed(2)}</td>
                    <td className={`text-right font-mono font-semibold ${filteredInvoices.reduce((s, i) => s + Math.max(0, i.remaining || 0), 0) > 0 ? 'text-red-600' : 'text-zinc-400'}`}>€{filteredInvoices.reduce((s, i) => s + Math.max(0, i.remaining || 0), 0).toFixed(2)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            )}
            </div>
          </div>
        )}

        {/* ====== PAYMENTS TAB ====== */}
        {activeTab === 'payments' && (
          <div className="space-y-4" data-testid="apt-payments-tab">
            {/* Header with Record Payment button */}
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-zinc-800">{t('payments')}</h4>
              {canPaymentCreate && (
                <Button onClick={openPaymentDialog} size="sm" className="bg-zinc-950 rounded-none text-xs" data-testid="record-payment-btn">
                  <Plus size={14} className="mr-1" />{t('recordPayment')}
                </Button>
              )}
            </div>
            {/* Payment Date Filter */}
            <div className="flex flex-col sm:flex-row flex-wrap items-end gap-3 bg-white border border-zinc-200 p-3 mb-3" data-testid="pay-date-filter">
              <div>
                <Label className="text-xs text-zinc-500">{t('period')}</Label>
                <Select value={payFilterType} onValueChange={(val) => applyPayQuickFilter(val)}>
                  <SelectTrigger className="w-[180px] rounded-none h-9 text-sm mt-1" data-testid="pay-filter-select">
                    <SelectValue placeholder={t('allTime')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allTime')}</SelectItem>
                    <SelectItem value="thisMonth">{t('thisMonth')}</SelectItem>
                    <SelectItem value="lastMonth">{t('lastMonth')}</SelectItem>
                    <SelectItem value="thisYear">{t('thisYear')}</SelectItem>
                    <SelectItem value="custom">{t('customRange')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(payFilterType === 'custom' || payDateFrom || payDateTo) && (
                <>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('dateFrom')}</Label>
                    <DatePickerDMY value={payDateFrom} onChange={v => { setPayDateFrom(v); setPayFilterType('custom'); }} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="pay-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('dateTo')}</Label>
                    <DatePickerDMY value={payDateTo} onChange={v => { setPayDateTo(v); setPayFilterType('custom'); }} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="pay-date-to" />
                  </div>
                  <Button type="button" variant="outline" onClick={() => applyPayQuickFilter('all')} className="rounded-none h-9 text-xs" data-testid="pay-clear-filter">{t('clearFilter')}</Button>
                </>
              )}
              {payDateFrom && <span className="text-xs text-zinc-400 self-center ml-auto">{dateFilteredPayments.length} / {payments.length}</span>}
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
            {dateFilteredPayments.length === 0 ? (
              <div className="p-12 text-center text-zinc-400">{t('noPayments')}</div>
            ) : (
              <table className="excel-table">
                <thead>
                  <tr>
                    <th className="text-xs">{t('date')}</th>
                    <th className="text-xs">{t('receiptNumber')}</th>
                    <th className="text-xs">{t('paymentMethod')}</th>
                    <th className="text-xs text-right">{t('received')}</th>
                    <th className="text-xs text-right">{t('allocated')}</th>
                    <th className="text-xs text-right">{t('availableCredit')}</th>
                    <th className="text-xs">{t('status')}</th>
                    <th className="text-xs">{t('notes')}</th>
                    <th className="text-xs">{language === 'el' ? 'Ιδιοκτήτης' : 'Owner'}</th>
                    <th className="text-xs w-28">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {dateFilteredPayments.map(p => {
                    const received = Math.abs(p.amount || 0);
                    const allocated = Number(p.allocated_amount || 0);
                    const credit = Number(p.unallocated_amount || 0);
                    const hasCredit = credit > 0.01;
                    return (
                    <tr key={p.payment_id} data-testid={`apt-pay-${p.payment_id}`}>
                      <td className="font-mono">{formatDateDMY(p.date)}</td>
                      <td className="font-mono text-sm">
                        {(() => {
                          const rcpNum = p.receipt_number || (p.notes && p.notes.match(/\[RCP-\d+\]/) ? p.notes.match(/\[RCP-\d+\]/)[0].replace('[','').replace(']','') : '');
                          return rcpNum ? (
                            <button onClick={() => openEditReceipt(rcpNum, p.payment_id)} className="text-blue-700 hover:text-blue-900 hover:underline cursor-pointer font-semibold" data-testid={`receipt-link-${p.payment_id}`}>{rcpNum}</button>
                          ) : '-';
                        })()}
                      </td>
                      <td className="text-sm">
                        {p.payment_source === 'bank' ? (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold">{t('bank')}</span>
                        ) : p.payment_source === 'credit' ? (
                          <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-200 text-xs font-semibold">{t('credit')}</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-semibold">{t('cash')}</span>
                        )}
                      </td>
                      <td className="font-mono font-semibold text-right text-emerald-700" data-testid={`apt-pay-received-${p.payment_id}`}>&euro;{received.toFixed(2)}</td>
                      <td className="font-mono text-right text-zinc-700" data-testid={`apt-pay-allocated-${p.payment_id}`}>&euro;{allocated.toFixed(2)}</td>
                      <td className={`font-mono text-right ${hasCredit ? 'font-semibold text-indigo-700' : 'text-zinc-400'}`} data-testid={`apt-pay-credit-${p.payment_id}`}>
                        {hasCredit ? `€${credit.toFixed(2)}` : '—'}
                      </td>
                      <td>
                        <InvoiceStatusBadge status="paid" testId={`apt-pay-status-${p.payment_id}`} />
                      </td>
                      <td className="text-sm text-zinc-500">{p.notes || '-'}</td>
                      <td className="text-sm whitespace-nowrap" data-testid={`apt-pay-owner-${p.payment_id}`}>
                        {(() => {
                          if (!p.owner_history_id) return <span className="text-zinc-400 italic">{language === 'el' ? 'Μη συνδεδεμένο' : 'Not Linked'}</span>;
                          const oh = (ownerHistoryList || []).find((o) => o.owner_history_id === p.owner_history_id);
                          const name = oh ? `${oh.first_name || ''} ${oh.last_name || ''}`.trim() : (p.owner_name_snapshot || '');
                          return (
                            <span className="flex items-center gap-1">
                              <span>{name || '—'}</span>
                              {oh && oh.status !== 'active' && <span className="text-[9px] uppercase px-1 bg-zinc-100 text-zinc-500">{language === 'el' ? 'Προηγ.' : 'Prev.'}</span>}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                          <div className="flex gap-0.5">
                            <button onClick={() => openPdfInBrowser(`/apartment-payments/${p.payment_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('view')} data-testid={`view-pay-${p.payment_id}`}><Eye size={14} /></button>
                            <button onClick={() => downloadPdf(`/apartment-payments/${p.payment_id}/pdf`, {}, `Receipt_${p.receipt_number || apartment.apartment_number || ''}_${p.date || ''}.pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={language === 'el' ? 'Λήψη PDF' : 'Download PDF'} data-testid={`download-pay-${p.payment_id}`}><DownloadSimple size={14} /></button>
                            <button onClick={() => printPdf(`/apartment-payments/${p.payment_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('printPdf')} data-testid={`print-pay-${p.payment_id}`}><Printer size={14} /></button>
                            <button onClick={() => sendReceiptEmail(p)} className="p-1 hover:bg-purple-50 text-zinc-400 hover:text-purple-600 transition-colors" title={t('sendByEmail')} data-testid={`email-pay-${p.payment_id}`}><EnvelopeSimple size={14} /></button>
                            <button onClick={() => setEmailHistPay(p)} className="p-1 hover:bg-purple-50 text-zinc-400 hover:text-purple-600 transition-colors" title={language === 'el' ? 'Ιστορικό αποστολών / Επαναποστολή' : 'Email history / Resend'} data-testid={`email-history-pay-${p.payment_id}`}><Clock size={14} /></button>
                            {canPaymentEdit && (
                              <button onClick={() => openEditPayment(p)} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`edit-pay-${p.payment_id}`}><PencilSimple size={14} /></button>
                            )}
                            {canPaymentDelete && (
                              <button onClick={() => { setDeletingPay(p); setDeletePayDialogOpen(true); }} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-pay-${p.payment_id}`}><Trash size={14} /></button>
                            )}
                          </div>
                        </td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-100 font-semibold border-t border-zinc-300">
                    <td colSpan="3" className="text-right">{t('total')}</td>
                    <td className="font-mono text-right text-emerald-700">&euro;{dateFilteredPayments.reduce((s, p) => s + Math.abs(p.amount || 0), 0).toFixed(2)}</td>
                    <td className="font-mono text-right text-zinc-700">&euro;{dateFilteredPayments.reduce((s, p) => s + Number(p.allocated_amount || 0), 0).toFixed(2)}</td>
                    <td className="font-mono text-right text-indigo-700">&euro;{dateFilteredPayments.reduce((s, p) => s + Number(p.unallocated_amount || 0), 0).toFixed(2)}</td>
                    <td></td>
                    <td></td>
                    <td></td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            )}
            </div>
          </div>
        )}

        {/* ====== CREDIT NOTES TAB ====== */}
        {activeTab === 'creditNotes' && (
          <div className="space-y-4" data-testid="apt-credit-notes-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('creditNotes')}</h2>
              <Button onClick={openCreditDialog} className="bg-zinc-950 rounded-none" data-testid="create-cn-btn">
                <Plus size={16} className="mr-1.5" />{t('createCreditNote')}
              </Button>
            </div>
            {/* Quick Filters */}
            <div className="flex flex-col sm:flex-row flex-wrap items-end gap-3 bg-white border border-zinc-200 p-3 mb-3" data-testid="cn-date-filter">
              <div>
                <Label className="text-xs text-zinc-500">{t('period')}</Label>
                <Select value={cnFilterType} onValueChange={(val) => applyCnQuickFilter(val)}>
                  <SelectTrigger className="w-[180px] rounded-none h-9 text-sm mt-1" data-testid="cn-filter-select">
                    <SelectValue placeholder={t('allTime')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('allTime')}</SelectItem>
                    <SelectItem value="thisMonth">{t('thisMonth')}</SelectItem>
                    <SelectItem value="lastMonth">{t('lastMonth')}</SelectItem>
                    <SelectItem value="thisYear">{t('thisYear')}</SelectItem>
                    <SelectItem value="custom">{t('customRange')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(cnFilterType === 'custom' || cnDateFrom || cnDateTo) && (
                <>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('dateFrom')}</Label>
                    <DatePickerDMY value={cnDateFrom} onChange={v => { setCnDateFrom(v); setCnFilterType('custom'); }} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="cn-date-from" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-500">{t('dateTo')}</Label>
                    <DatePickerDMY value={cnDateTo} onChange={v => { setCnDateTo(v); setCnFilterType('custom'); }} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="cn-date-to" />
                  </div>
                  <Button type="button" variant="outline" onClick={() => applyCnQuickFilter('all')} className="rounded-none h-9 text-xs" data-testid="cn-clear-filter">{t('clearFilter')}</Button>
                </>
              )}
            </div>
            {/* Credit Notes Table */}
            {filteredCreditNotes.length === 0 ? (
              <div className="text-center py-12 text-zinc-400" data-testid="no-credit-notes">{t('noCreditNotes')}</div>
            ) : (
              <div className="bg-white border border-zinc-200 overflow-hidden">
                <table className="w-full text-sm" data-testid="credit-notes-table">
                  <thead><tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-500">{t('date')}</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-500">{t('creditNoteNumber')}</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-zinc-500">{t('amount')}</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-zinc-500">{t('description')}</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-zinc-500">{t('actions')}</th>
                  </tr></thead>
                  <tbody>
                    {filteredCreditNotes.map(cn => (
                      <tr key={cn.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`cn-row-${cn.invoice_id}`}>
                        <td className="px-4 py-2.5 font-mono text-sm">{(cn.created_at || cn.period || '').slice(0, 10)}</td>
                        <td className="px-4 py-2.5 font-mono text-xs font-semibold text-zinc-700" data-testid={`cn-number-${cn.invoice_id}`}>{cn.credit_note_number || cn.invoice_id}</td>
                        <td className="px-4 py-2.5 font-mono font-semibold text-right text-purple-700">&euro;{Math.abs(cn.amount || 0).toFixed(2)}</td>
                        <td className="px-4 py-2.5 text-zinc-600">{cn.description || '-'}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex justify-end gap-0.5">
                            <button onClick={() => openPdfInBrowser(`/apartments/${apartmentId}/credit-notes/${cn.invoice_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('view')} data-testid={`view-cn-${cn.invoice_id}`}><Eye size={14} /></button>
                            <button onClick={() => printPdf(`/apartments/${apartmentId}/credit-notes/${cn.invoice_id}/pdf`)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600 transition-colors" title={t('printPdf')} data-testid={`print-cn-${cn.invoice_id}`}><Printer size={14} /></button>
                            <button onClick={() => openEmailDialog(
                              `/api/apartments/${apartmentId}/credit-notes/${cn.invoice_id}/pdf`,
                              `CreditNote_${cn.credit_note_number || cn.invoice_id}.pdf`,
                              {
                                templateType: 'invoice',
                                dynamicFields: {
                                  apartment_number: apartment.apartment_number || '',
                                  owner_name: apartment.owner_name || '',
                                  building_name: apartment.building_name || '',
                                  invoice_number: cn.credit_note_number || '',
                                },
                                email: apartment.email || '',
                              }
                            )} className="p-1 hover:bg-purple-50 text-zinc-400 hover:text-purple-600 transition-colors" title={t('sendByEmail')} data-testid={`email-cn-${cn.invoice_id}`}><EnvelopeSimple size={14} /></button>
                            {isAdmin && (
                              <>
                                <button onClick={() => { setEditingCn(cn); setEditCnForm({ amount: Math.abs(cn.amount || 0), description: cn.description || '', date: (cn.created_at || cn.period || '').slice(0, 10) }); setEditCnDialogOpen(true); }} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700 transition-colors" title={t('edit')} data-testid={`edit-cn-${cn.invoice_id}`}><PencilSimple size={14} /></button>
                                <button onClick={async () => { if (window.confirm(t('confirmDelete'))) { try { await axios.delete(`${API}/apartments/${apartmentId}/credit-notes/${cn.invoice_id}`); toast.success(t('creditNoteDeleted')); fetchAll(); } catch { toast.error(t('errorOccurred')); }}}} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600 transition-colors" title={t('delete')} data-testid={`delete-cn-${cn.invoice_id}`}><Trash size={14} /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="bg-zinc-50 border-t border-zinc-200">
                    <td colSpan="2" className="px-4 py-2.5 font-semibold text-sm">{t('total')}</td>
                    <td className="px-4 py-2.5 font-mono font-bold text-right text-purple-700">&euro;{filteredCreditNotes.reduce((s, cn) => s + Math.abs(cn.amount || 0), 0).toFixed(2)}</td>
                    <td colSpan="2"></td>
                  </tr></tfoot>
                </table>
              </div>
            )}
          </div>
        )}


        {/* ====== STATEMENT TAB ====== */}
        {activeTab === 'statement' && statement && (
          <div className="space-y-4" data-testid="apt-statement-tab">
            {/* Statement Header with single "View PDF" action (Phase 209x).
                Phase 209x — Removed the "Save Statement" button + the
                "Export PDF" dropdown (View / Print + Download). One clean
                "View PDF" button now opens the statement inline in a new
                tab; from there the user can print or save manually. The
                "Send by Email" button stays. */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{t('accountStatement')}</h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="rounded-none" onClick={handlePrintStatementPdf} data-testid="view-pdf-btn">
                  <FilePdf size={16} className="mr-1.5" />{t('viewPdf') || 'View PDF'}
                </Button>
                {isAdmin && (
                  <Button onClick={handleEmailStatement} className="bg-zinc-950 rounded-none" data-testid="email-statement-btn">
                    <EnvelopeSimple size={16} className="mr-1.5" />{t('sendByEmail')}
                  </Button>
                )}
              </div>
            </div>

            {/* Date Range + Owner Filter */}
            <div className="flex flex-col sm:flex-row flex-wrap items-end gap-3 bg-white border border-zinc-200 p-3 mb-3" data-testid="stmt-date-filter">
              <div>
                <Label className="text-xs text-zinc-500">{t('dateFrom')}</Label>
                <DatePickerDMY value={stmtDateFrom} onChange={v => setStmtDateFrom(v)} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="stmt-date-from" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('dateTo')}</Label>
                <DatePickerDMY value={stmtDateTo} onChange={v => setStmtDateTo(v)} className="rounded-none h-9 text-sm font-mono mt-1 w-[150px]" data-testid="stmt-date-to" />
              </div>
              {/* Phase 702 — Owner selector. Only rendered when the apartment
                  has Owner History. "All Owners" = no filter (default). */}
              {ownerHistoryList.length > 0 && (
                <div>
                  <Label className="text-xs text-zinc-500">{t('owner')}</Label>
                  <select
                    value={stmtOwnerHistoryId}
                    onChange={(e) => setStmtOwnerHistoryId(e.target.value)}
                    className="rounded-none h-9 text-sm font-mono mt-1 w-[240px] border border-zinc-300 bg-white px-2"
                    data-testid="stmt-owner-select"
                  >
                    <option value="">{t('allOwners') || 'All Owners'}</option>
                    {ownerHistoryList.map(oh => {
                      const name = [oh.first_name, oh.last_name].filter(Boolean).join(' ') || oh.owner_name || '—';
                      const period = oh.start_date
                        ? `${formatDateDMY(oh.start_date)}${oh.end_date ? ' – ' + formatDateDMY(oh.end_date) : ' – ' + (t('present') || 'present')}`
                        : '';
                      return (
                        <option key={oh.owner_history_id} value={oh.owner_history_id}>
                          {name}{period ? ` (${period})` : ''}{oh.status === 'active' ? ' • ' + (t('active') || 'active') : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              {(stmtDateFrom || stmtDateTo || stmtOwnerHistoryId) && (
                <Button type="button" variant="outline" onClick={() => { setStmtDateFrom(''); setStmtDateTo(''); setStmtOwnerHistoryId(''); }} className="rounded-none h-9 text-xs" data-testid="stmt-clear-filter">{t('clearFilter')}</Button>
              )}
            </div>

            {/* Phase 297 — Summary cards moved to BOTTOM of the page (below
                the transactions table) as a compact accounting footer. See
                bottom of this tab for the footer rendering. */}

            {/* Statement Table */}
            <div className="bg-white border border-zinc-200 overflow-x-auto">
              <table className="excel-table">
                <thead>
                  <tr className="bg-zinc-50">
                    <th className="text-xs">{t('date')}</th>
                    <th className="text-xs">{t('period')}</th>
                    <th className="text-xs">{t('reference')}</th>
                    <th className="text-xs">{t('description')}</th>
                    <th className="text-xs text-right">{t('charges')}</th>
                    <th className="text-xs text-right">{t('payments')}</th>
                    <th className="text-xs text-right">{t('balance')}</th>
                    <th className="text-xs text-center w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Phase 985 — Statement running balance is now computed
                    // PER OWNER SEGMENT so the balance column reads correctly
                    // for multi-owner statements. The chronological order is
                    // preserved within each segment, but segments are grouped
                    // (Themis rows first, Marios rows next, etc.) so the
                    // running balance never mixes across owners.
                    //
                    // Sign convention (unchanged): running -= charge and
                    // running += payment/credit → running_balance is
                    // NEGATIVE when the owner owes money, POSITIVE when
                    // they have credit. The balance column displays the
                    // raw signed value (colored red when < 0).
                    //
                    // For single-owner statements (no banner) we fall back
                    // to a continuous chronological running so the classic
                    // ledger reads top-to-bottom.
                    const baseEntries = statement.entries || [];
                    // 1) Sort chronologically (Opening Balance pinned first on date ties).
                    const sortedEntries = [...baseEntries].sort((a, b) => {
                      const da = a.date || '';
                      const db = b.date || '';
                      if (da !== db) return da < db ? -1 : 1;
                      if (a.is_opening_balance && !b.is_opening_balance) return -1;
                      if (!a.is_opening_balance && b.is_opening_balance) return 1;
                      return 0;
                    });
                    // 2) Detect banner mode based on distinct owner periods.
                    const periodMeta = {};
                    (statement.owner_periods || []).forEach(p => { periodMeta[p.owner_history_id] = p; });
                    const distinctPeriodIds = new Set(
                      sortedEntries.map(r => r.owner_period_id).filter(Boolean)
                    );
                    const showOwnerBanners = distinctPeriodIds.size > 1;
                    // 3) Build the display list. When banners are shown we
                    //    group by owner_period ordered by start_date and
                    //    compute a fresh running per segment. Otherwise we
                    //    keep the classic continuous chronological running.
                    const applyDelta = (r, e) => {
                      if (e.type === 'charge') return r - (e.amount || 0);
                      if (e.type === 'credit_note') return r + (e.amount || 0);
                      if (e.type === 'payment') return r + (e.amount || 0);
                      return r;
                    };
                    let display = [];
                    if (showOwnerBanners) {
                      const segmentOrder = [...distinctPeriodIds].sort((a, b) => {
                        const sa = (periodMeta[a]?.start_date || '');
                        const sb = (periodMeta[b]?.start_date || '');
                        if (sa === sb) return 0;
                        return sa < sb ? -1 : 1;
                      });
                      segmentOrder.forEach(segId => {
                        const segEntries = sortedEntries.filter(e => (e.owner_period_id || '') === segId);
                        let running = 0;
                        segEntries.forEach(e => {
                          running = applyDelta(running, e);
                          display.push({ entry: e, running });
                        });
                      });
                      // Orphan rows (no owner_period_id — legacy fallback)
                      // land at the end as their own implicit group so they
                      // never silently disappear.
                      const orphans = sortedEntries.filter(e => !e.owner_period_id);
                      if (orphans.length) {
                        let running = 0;
                        orphans.forEach(e => {
                          running = applyDelta(running, e);
                          display.push({ entry: e, running });
                        });
                      }
                    } else {
                      let running = 0;
                      sortedEntries.forEach(e => {
                        running = applyDelta(running, e);
                        display.push({ entry: e, running });
                      });
                    }
                    const fmtDmy = (d) => {
                      if (!d) return '—';
                      const parts = String(d).split('-');
                      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d;
                    };
                    // Phase 986 — Compute the EFFECTIVE end date per owner
                    // period for display purposes. Ownership period should
                    // stop the day before the next owner starts, regardless
                    // of what stored end_date says. This preserves accounting
                    // reality when a payment/invoice was later linked to a
                    // previous owner via allocations — the ownership period
                    // itself is not extended by the transaction date.
                    const effectiveEndByPid = {};
                    (() => {
                      const sortedPeriods = (statement.owner_periods || [])
                        .slice()
                        .sort((a, b) => (a.start_date || '').localeCompare(b.start_date || ''));
                      const shiftBackOneDay = (yyyymmdd) => {
                        if (!yyyymmdd || !/^\d{4}-\d{2}-\d{2}/.test(yyyymmdd)) return yyyymmdd;
                        const d = new Date(yyyymmdd.slice(0, 10) + 'T12:00:00Z');
                        d.setUTCDate(d.getUTCDate() - 1);
                        return d.toISOString().slice(0, 10);
                      };
                      sortedPeriods.forEach((p, idx) => {
                        const next = sortedPeriods[idx + 1];
                        const stored = (p.end_date || '').slice(0, 10);
                        const capBy = next && next.start_date ? shiftBackOneDay(next.start_date.slice(0, 10)) : '';
                        let eff = '';
                        if (stored && capBy) eff = stored < capBy ? stored : capBy;
                        else eff = capBy || stored || '';
                        effectiveEndByPid[p.owner_history_id] = eff;
                      });
                    })();
                    let lastPeriodId = null;
                    let segmentCharges = 0;
                    let segmentPayments = 0;
                    let segmentClosing = 0;
                    // Phase 986 — Track whether the current segment received
                    // an explicit Opening Balance row. If not, we prepend a
                    // synthetic "Opening Balance: €0.00" row so the visual
                    // model matches the user's mental model of "new owner
                    // starts at zero".
                    let currentSegmentHasOB = false;
                    const flushSegmentSubtotal = () => {
                      if (!showOwnerBanners || !lastPeriodId) return;
                      const p = periodMeta[lastPeriodId];
                      const ownerLabel = p ? (p.owner_name || '—') : '—';
                      out.push(
                        <tr key={`period-subtotal-${lastPeriodId}`} className="bg-zinc-50 border-t border-zinc-200" data-testid={`stmt-owner-subtotal-${lastPeriodId}`}>
                          <td colSpan={4} className="text-right text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-1.5">
                            {(t('subtotal') || 'Subtotal')} · {ownerLabel}
                          </td>
                          <td className="font-mono text-right text-zinc-700 text-sm font-semibold">&euro;{segmentCharges.toFixed(2)}</td>
                          <td className="font-mono text-right text-zinc-700 text-sm font-semibold">&euro;{segmentPayments.toFixed(2)}</td>
                          <td className={`font-mono text-right text-sm font-bold ${segmentClosing < 0 ? 'text-red-700' : 'text-zinc-700'}`} data-testid={`stmt-owner-subtotal-balance-${lastPeriodId}`}>&euro;{segmentClosing.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      );
                      segmentCharges = 0;
                      segmentPayments = 0;
                      segmentClosing = 0;
                    };
                    const out = [];
                    display.forEach(({ entry, running }, i) => {
                    const isOB = entry.is_opening_balance;
                    const isCN = entry.type === 'credit_note';
                    const desc = language === 'el' && entry.description_el ? entry.description_el : entry.description;
                    // Phase 211 — Per-row View button: links each line to its
                    // own PDF (invoice / payment receipt / credit note). The
                    // Opening Balance row has nothing to view.
                    let viewPath = null;
                    if (!isOB) {
                      if (entry.type === 'charge' && entry.invoice_id) viewPath = `/apartments/${apartmentId}/invoices/${entry.invoice_id}/pdf`;
                      else if (entry.type === 'credit_note' && entry.invoice_id) viewPath = `/apartments/${apartmentId}/credit-notes/${entry.invoice_id}/pdf`;
                      else if (entry.type === 'payment' && entry.payment_id) viewPath = `/apartment-payments/${entry.payment_id}/pdf`;
                    }
                    // Emit owner-period banner before this row if the period changed
                    // AND there's an actual ownership transition in the visible rows.
                    const pid = entry.owner_period_id;
                    if (showOwnerBanners && pid && pid !== lastPeriodId && (statement.owner_periods || []).length > 0) {
                      // Flush the previous segment's subtotal first so each
                      // owner block ends with a clear €X charges / €Y payments
                      // / €Z closing balance summary line.
                      flushSegmentSubtotal();
                      const p = periodMeta[pid] || (statement.owner_periods || [])[0];
                      if (p) {
                        // Phase 986 — Use the EFFECTIVE end date (capped at
                        // the next owner's start - 1 day) so a payment
                        // linked back to a previous owner never extends
                        // their ownership period label.
                        const effEnd = effectiveEndByPid[pid] || '';
                        const endLabel = effEnd ? fmtDmy(effEnd) : (t('statusActive') || 'Active');
                        out.push(
                          <tr key={`period-banner-${pid}-${i}`} className="bg-zinc-100 border-t border-b border-zinc-200" data-testid={`stmt-owner-period-${pid}`}>
                            <td colSpan={8} className="px-4 py-2">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-[10px] uppercase tracking-wider text-zinc-500">{t('ownerLabel') || 'Owner'}</span>
                                  <span className="font-semibold text-zinc-900 text-sm" data-testid={`stmt-owner-period-name-${pid}`}>{p.owner_name || '—'}</span>
                                </div>
                                <span className="text-[11px] text-zinc-500 font-mono">
                                  {t('ownershipPeriod') || 'Ownership Period'}: {fmtDmy(p.start_date)} → {endLabel}
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                        lastPeriodId = pid;
                        currentSegmentHasOB = false;
                        // Phase 986 — Prepend an explicit "Opening Balance
                        // €0.00" row for this segment. If the first data row
                        // is itself the OB entry, we swap this synthetic row
                        // out to avoid duplicates (handled by the isOB flag
                        // check below).
                      }
                    }
                    if (isOB) currentSegmentHasOB = true;
                    // If this is the segment's first data row and it is NOT
                    // an OB entry, emit a synthetic Opening Balance €0.00
                    // row RIGHT BEFORE it so the new owner's ledger visibly
                    // starts from zero (no carry-forward from the previous
                    // owner). We detect first-of-segment by checking that
                    // segmentCharges + segmentPayments is still 0.
                    if (showOwnerBanners && !isOB && lastPeriodId === pid && !currentSegmentHasOB && segmentCharges === 0 && segmentPayments === 0) {
                      out.push(
                        <tr key={`segment-ob-${pid}-${i}`} className="border-t border-zinc-100" data-testid={`stmt-segment-ob-${pid}`}>
                          <td className="font-mono text-sm text-zinc-400">—</td>
                          <td className="font-mono text-xs text-zinc-400">—</td>
                          <td className="font-mono text-sm text-zinc-400">—</td>
                          <td className="text-sm italic text-zinc-500">{t('openingBalance') || 'Opening Balance'}</td>
                          <td className="font-mono text-right text-zinc-400">—</td>
                          <td className="font-mono text-right text-zinc-400">—</td>
                          <td className="font-mono text-right font-semibold text-zinc-500">&euro;0.00</td>
                          <td></td>
                        </tr>
                      );
                      currentSegmentHasOB = true;
                    }
                    // Accumulate segment totals for the pending subtotal row.
                    if (entry.type === 'charge') segmentCharges += (entry.amount || 0);
                    else if (entry.type === 'credit_note') segmentCharges -= (entry.amount || 0);
                    else if (entry.type === 'payment') segmentPayments += (entry.amount || 0);
                    segmentClosing = running;
                    out.push(
                      <tr key={i} data-testid={`stmt-row-${i}`}>
                        <td className="font-mono text-sm">{isOB && !entry.date ? '—' : formatDateDMY(entry.date)}</td>
                        <td className="font-mono text-xs text-zinc-600" data-testid={`stmt-period-${i}`}>{entry.period || '—'}</td>
                        <td className="font-mono text-sm text-zinc-700 font-semibold" data-testid={`stmt-ref-${i}`}>{entry.reference || ''}</td>
                        <td className={`text-sm ${isOB ? 'font-semibold text-zinc-800' : 'text-zinc-700'}`}>{desc}</td>
                        <td className="font-mono text-right text-zinc-700">{entry.type === 'charge' ? `€${(entry.amount || 0).toFixed(2)}` : isCN ? `(€${(entry.amount || 0).toFixed(2)})` : ''}</td>
                        <td className="font-mono text-right text-zinc-700">{entry.type === 'payment' ? `€${(entry.amount || 0).toFixed(2)}` : ''}</td>
                        <td className={`font-mono text-right font-semibold ${running < 0 ? 'text-red-600' : 'text-zinc-700'}`}>&euro;{running.toFixed(2)}</td>
                        <td className="text-center">
                          {viewPath && (
                            <button
                              type="button"
                              onClick={() => openPdfInBrowser(viewPath)}
                              className="text-zinc-300 hover:text-zinc-700 transition-colors"
                              title={t('view') || 'View'}
                              data-testid={`view-stmt-row-${i}`}
                            >
                              <Eye size={14} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                    });
                    // Emit the pending subtotal row for the very last segment.
                    flushSegmentSubtotal();
                    return out;
                  })()}
                </tbody>
                <tfoot>
                  <tr className="bg-zinc-100 font-semibold border-t-2 border-zinc-300">
                    <td colSpan={4} className="text-right">{t('total')}</td>
                    <td className="font-mono text-right text-zinc-700">&euro;{(totalCharges - (statement?.total_credit_notes || 0)).toFixed(2)}</td>
                    <td className="font-mono text-right text-zinc-700">&euro;{totalPayments.toFixed(2)}</td>
                    <td className={`font-mono text-right font-bold ${finalBalance < 0 ? 'text-red-700' : 'text-zinc-700'}`}>&euro;{finalBalance.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Phase 297 — Compact accounting-style summary footer at the
                bottom of the statement (was previously a card grid at top). */}
            <div className="bg-white border border-zinc-200" data-testid="stmt-summary-footer">
              <div className="px-4 py-2 border-b border-zinc-100 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{t('summary') || 'Summary'}</span>
                {(statement.credit_balance || 0) > 0 && (
                  <button
                    type="button"
                    onClick={openCreditAllocDialog}
                    className="text-[10px] uppercase tracking-wider text-zinc-700 hover:text-zinc-900 underline-offset-2 hover:underline"
                    data-testid="footer-apply-credit-btn"
                  >
                    {t('applyCredit')} &euro;{(statement.credit_balance || 0).toFixed(2)}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-5 divide-x divide-zinc-100 text-sm">
                <div className="px-3 py-2 flex flex-col" data-testid="stmt-opening-balance-card">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">{t('openingBalance')}</span>
                  <span className={`font-mono text-sm font-semibold mt-0.5 ${openingBalance > 0 ? 'text-red-600' : 'text-zinc-700'}`}>
                    {openingBalance < 0 ? '-' : ''}&euro;{Math.abs(openingBalance).toFixed(2)}
                  </span>
                </div>
                <div className="px-3 py-2 flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">{t('totalCharges')}</span>
                  <span className="font-mono text-sm font-semibold text-zinc-700 mt-0.5">&euro;{totalCharges.toFixed(2)}</span>
                </div>
                <div className="px-3 py-2 flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">{t('totalPayments')}</span>
                  <span className="font-mono text-sm font-semibold text-zinc-700 mt-0.5">&euro;{totalPayments.toFixed(2)}</span>
                </div>
                <div className="px-3 py-2 flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">{t('remainingBalance')}</span>
                  <span className={`font-mono text-sm font-semibold mt-0.5 ${finalBalance < 0 ? 'text-red-600' : 'text-zinc-700'}`}>&euro;{finalBalance.toFixed(2)}</span>
                </div>
                <div className="px-3 py-2 flex flex-col" data-testid="stmt-credit-balance">
                  <span className="text-[10px] uppercase tracking-wider text-zinc-400">{t('availableCredit')}</span>
                  <span className="font-mono text-sm font-semibold text-zinc-700 mt-0.5">&euro;{(statement.credit_balance || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Saved Statements — Collapsible */}
            {savedStatements.length > 0 && (
              <div className="bg-white border border-zinc-200 mt-2" data-testid="saved-statements-section">
                <button
                  onClick={() => setSavedStmtOpen(!savedStmtOpen)}
                  className="w-full px-4 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors text-left"
                  data-testid="toggle-saved-stmts"
                >
                  <FileText size={14} className="text-zinc-500" />
                  <span className="text-xs font-semibold text-zinc-700 flex-1">{t('savedStatements')}</span>
                  <span className="text-[10px] text-zinc-400 font-mono">{savedStatements.length}</span>
                  {savedStmtOpen ? <CaretUp size={12} className="text-zinc-400" /> : <CaretDown size={12} className="text-zinc-400" />}
                </button>
                {savedStmtOpen && (
                  <div className="divide-y divide-zinc-100 border-t border-zinc-100">
                    {savedStatements.map(ss => {
                      const dateLabel = ss.date_from && ss.date_to
                        ? `${formatDateDMY(ss.date_from)} – ${formatDateDMY(ss.date_to)}`
                        : ss.date_from ? `${t('from')} ${formatDateDMY(ss.date_from)}` : ss.date_to ? `${t('to')} ${formatDateDMY(ss.date_to)}` : t('allTime');
                      const qs = [ss.date_from && `date_from=${ss.date_from}`, ss.date_to && `date_to=${ss.date_to}`, ss.owner_history_id && `owner_history_id=${ss.owner_history_id}`].filter(Boolean).join('&');
                      const pdfParams = qs ? Object.fromEntries(new URLSearchParams(qs)) : {};
                      return (
                        <div key={ss.statement_id} className="flex items-center px-4 py-2 hover:bg-zinc-50 transition-colors" data-testid={`saved-stmt-${ss.statement_id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium">#{ss.apartment_number} — {dateLabel}</p>
                            <p className="text-[10px] text-zinc-400">{t('balance')}: <span className={`font-mono font-semibold ${ss.closing_balance < 0 ? 'text-red-600' : 'text-emerald-600'}`}>&euro;{(ss.closing_balance || 0).toFixed(2)}</span></p>
                          </div>
                          <div className="flex items-center gap-0.5 flex-shrink-0">
                            <button onClick={() => openPdfInBrowser(`/apartments/${apartmentId}/statement/pdf`, pdfParams)} className="p-1.5 hover:bg-blue-50 text-zinc-400 hover:text-blue-600" title={t('view')} data-testid={`view-saved-stmt-${ss.statement_id}`}><Eye size={13} /></button>
                            <button onClick={() => downloadPdf(`/apartments/${apartmentId}/statement/pdf`, pdfParams)} className="p-1.5 hover:bg-blue-50 text-zinc-400 hover:text-blue-600" title={t('downloadPdf')} data-testid={`dl-saved-stmt-${ss.statement_id}`}><FilePdf size={13} /></button>
                            <button onClick={() => {
                              const pdfPath = `/api/apartments/${apartmentId}/statement/pdf${qs ? '?' + qs : ''}`;
                              openEmailDialog(pdfPath, `Statement_${ss.apartment_number}_${dateLabel.replace(/\s/g, '_')}.pdf`, {
                                templateType: 'statement',
                                dynamicFields: {
                                  apartment_number: ss.apartment_number || '',
                                  owner_name: ss.owner_name || apartment?.owner_name || '',
                                  building_name: apartment?.building_name || '',
                                  start_date: ss.date_from ? formatDateDMY(ss.date_from) : '',
                                  end_date: ss.date_to ? formatDateDMY(ss.date_to) : '',
                                },
                                email: apartment?.email || '',
                              });
                            }} className="p-1.5 hover:bg-purple-50 text-zinc-400 hover:text-purple-600" title={t('sendByEmail')} data-testid={`email-saved-stmt-${ss.statement_id}`}><EnvelopeSimple size={13} /></button>
                            {isAdmin && (
                              <button onClick={() => handleDeleteSavedStatement(ss.statement_id)} className="p-1.5 hover:bg-red-50 text-zinc-400 hover:text-red-500" title={t('delete')} data-testid={`del-saved-stmt-${ss.statement_id}`}><Minus size={13} /></button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}


          </div>
        )}

        {/* ====== View Email Dialog — Phase 658: accessible from both Statement
            and Email History tabs. Body rendered as HTML so rich-text emails
            (bold / italic / lists / links) display correctly. ====== */}
        {viewingEmail && (
          <Dialog open={!!viewingEmail} onOpenChange={() => setViewingEmail(null)}>
            <DialogContent className="rounded-none w-[95vw] max-w-[900px] sm:!max-w-[900px] max-h-[88vh] flex flex-col p-0" data-testid="view-email-dialog">
              <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-200 shrink-0">
                <DialogTitle className="flex items-center gap-2 text-sm" style={{ fontFamily: 'var(--font-heading)' }}>
                  <EnvelopeSimple size={18} />{t('viewMessage') || 'View message'}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 text-sm">
                <div className="grid grid-cols-[90px_1fr] gap-y-1.5">
                  <span className="text-zinc-400 text-xs">{t('from') || 'From'}:</span>
                  <span className="text-xs">{viewingEmail.from_name || viewingEmail.sent_by || ''}{viewingEmail.from_email ? ` <${viewingEmail.from_email}>` : ''}</span>
                  <span className="text-zinc-400 text-xs">{t('to') || 'To'}:</span>
                  <span className="text-xs font-medium">{viewingEmail.to_email}</span>
                  {viewingEmail.cc && <><span className="text-zinc-400 text-xs">CC:</span><span className="text-xs">{viewingEmail.cc}</span></>}
                  {viewingEmail.bcc && <><span className="text-zinc-400 text-xs">BCC:</span><span className="text-xs">{viewingEmail.bcc}</span></>}
                  <span className="text-zinc-400 text-xs">{t('subject') || 'Subject'}:</span>
                  <span className="text-xs font-medium">{viewingEmail.subject}</span>
                  <span className="text-zinc-400 text-xs">{t('date') || 'Date'}:</span>
                  <span className="text-xs font-mono">{viewingEmail.sent_at ? new Date(viewingEmail.sent_at).toLocaleString('en-GB') : ''}</span>
                  <span className="text-zinc-400 text-xs">{t('status') || 'Status'}:</span>
                  <span className={`text-xs font-semibold ${viewingEmail.status === 'sent' ? 'text-emerald-700' : 'text-red-600'}`}>{viewingEmail.status === 'sent' ? (t('sent') || 'Sent') : (t('failed') || 'Failed')}</span>
                </div>
                <div className="border border-zinc-200 bg-white p-4">
                  <div
                    className="text-sm text-zinc-700 prose prose-sm max-w-none"
                    data-testid="email-body-view"
                    dangerouslySetInnerHTML={{ __html: viewingEmail.body || '' }}
                  />
                </div>
                {viewingEmail.filename && (
                  <div className="flex items-center justify-between gap-2 bg-zinc-50 border border-zinc-200 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FilePdf size={14} className="text-zinc-400 flex-shrink-0" />
                      <span className="text-[11px] text-zinc-500 truncate">{viewingEmail.filename}</span>
                    </div>
                    {viewingEmail.pdf_url && (
                      <button
                        onClick={async () => {
                          try {
                            const blobUrl = await getPdfBlobUrl(viewingEmail.pdf_url, { disposition: 'attachment' });
                            const a = document.createElement('a');
                            a.href = blobUrl;
                            a.download = viewingEmail.filename || 'attachment.pdf';
                            a.rel = 'noopener';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                          } catch (err) {
                            try {
                              const { toast } = await import('sonner');
                              toast.error(err?.message || 'Failed to download attachment');
                            } catch (_e) { /* ignore */ }
                          }
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                        data-testid="view-email-download-attachment"
                      >
                        <DownloadSimple size={12} /> {t('downloadAttachment') || 'Download'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        )}


        {activeTab === 'emailHistory' && (
          <div className="bg-white border border-zinc-200" data-testid="email-history-tab">
            <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <EnvelopeSimple size={14} className="text-zinc-500" />
                <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('emailHistory') || 'Email History'}</h3>
                <span className="text-[11px] text-zinc-400 font-mono">({emailHistory.length})</span>
              </div>
            </div>
            {emailHistory.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-zinc-400" data-testid="email-history-empty">
                {t('noEmailsYet') || 'No emails have been sent from this apartment yet.'}
              </div>
            ) : (
              <div className="divide-y divide-zinc-100">
                <div className="px-4 py-2 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-50">
                  <div className="col-span-2">{t('dateTime') || 'Date & Time'}</div>
                  <div className="col-span-3">{t('recipientsLabel') || 'Recipient(s)'}</div>
                  <div className="col-span-3">{t('subject') || 'Subject'}</div>
                  <div className="col-span-2">{t('senderEmail') || 'Sender'}</div>
                  <div className="col-span-1 text-center">{t('statusLabel') || 'Status'}</div>
                  <div className="col-span-1 text-right">{t('actions') || 'Actions'}</div>
                </div>
                {emailHistory.map(log => {
                  const sentDate = log.sent_at ? new Date(log.sent_at) : null;
                  const dateStr = sentDate ? `${sentDate.toLocaleDateString('en-GB')} ${sentDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : '';
                  return (
                    <div key={log.log_id} className="px-4 py-2.5 grid grid-cols-12 gap-2 items-center hover:bg-zinc-50 text-xs" data-testid={`email-log-${log.log_id}`}>
                      <div className="col-span-2 font-mono text-[11px] text-zinc-600">{dateStr}</div>
                      <div className="col-span-3 truncate text-zinc-700">
                        <div className="truncate">{log.to_email}</div>
                        {(log.cc || log.bcc) && <div className="truncate text-[10px] text-zinc-400">{log.cc ? `CC: ${log.cc}` : ''}{log.cc && log.bcc ? ' · ' : ''}{log.bcc ? `BCC: ${log.bcc}` : ''}</div>}
                      </div>
                      <div className="col-span-3 truncate text-zinc-700">{log.subject || '—'}</div>
                      <div className="col-span-2 truncate">
                        <div className="text-zinc-700 truncate">{log.from_name || log.sent_by || '—'}</div>
                        <div className="text-[10px] text-zinc-400 font-mono truncate">{log.from_email || ''}</div>
                      </div>
                      <div className="col-span-1 text-center">
                        <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-none ${log.status === 'sent' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                          {log.status === 'sent' ? (t('sent') || 'Sent') : (t('failed') || 'Failed')}
                        </span>
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-0.5">
                        <button onClick={() => setViewingEmail(log)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600" title={t('viewMessage') || 'View'} data-testid={`view-email-${log.log_id}`}><Eye size={13} /></button>
                        {log.pdf_url && (
                          <button
                            onClick={async () => {
                              try {
                                const blobUrl = await getPdfBlobUrl(log.pdf_url, { disposition: 'attachment' });
                                const a = document.createElement('a');
                                a.href = blobUrl;
                                a.download = log.filename || 'attachment.pdf';
                                a.rel = 'noopener';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                              } catch (err) {
                                try {
                                  const { toast } = await import('sonner');
                                  toast.error(err?.message || 'Failed to download attachment');
                                } catch (_e) { /* ignore */ }
                              }
                            }}
                            className="p-1 hover:bg-emerald-50 text-zinc-400 hover:text-emerald-600"
                            title={t('downloadAttachment') || 'Download attachment'}
                            data-testid={`download-email-${log.log_id}`}
                          >
                            <Paperclip size={13} />
                          </button>
                        )}
                        <button onClick={() => {
                          openEmailDialog(
                            log.pdf_url || '',
                            log.filename || 'document.pdf',
                            { templateType: log.doc_type || 'statement', dynamicFields: {}, email: log.to_email }
                          );
                        }} className="p-1 hover:bg-purple-50 text-zinc-400 hover:text-purple-600" title={t('resend') || 'Resend'} data-testid={`resend-email-${log.log_id}`}><EnvelopeSimple size={13} /></button>
                        {/* Phase 850 — Direct re-send for recurring-invoice emails. Uses the
                            server-side `/apartment-invoices/{invoice_id}/send-email` endpoint
                            which re-resolves recipients + regenerates the PDF, so the owner
                            always gets a fresh copy with the latest apartment config. */}
                        {log.doc_type === 'recurring_invoice' && log.invoice_id && (
                          <button
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/apartment-invoices/${log.invoice_id}/send-email`);
                                const st = res?.data?.status || 'sent';
                                const { toast } = await import('sonner');
                                if (st === 'sent') toast.success(t('emailSent') || 'Email sent');
                                else if (st === 'skipped_testing') toast.warning((t('emailKillSwitch') || 'Kill-switch active — no email dispatched'));
                                else if (st === 'mocked') toast.warning('Mocked (no Brevo key)');
                                else toast.error(res?.data?.error || 'Failed');
                                fetchEmailHistory();
                              } catch (e) {
                                const { toast } = await import('sonner');
                                toast.error(e?.response?.data?.detail || 'Failed to re-send');
                              }
                            }}
                            className="p-1 hover:bg-amber-50 text-zinc-400 hover:text-amber-600"
                            title={t('sendAgain') || 'Send Again'}
                            data-testid={`invoice-resend-direct-${log.log_id}`}
                          ><ArrowClockwise size={13} /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}



        {/* ====== NOTES TAB ====== */}
        {activeTab === 'ownerHistory' && (
          <OwnerHistoryTab
            apartmentId={apartmentId}
            apartmentNumber={apartment?.apartment_number}
            onChanged={fetchAll}
          />
        )}

        {activeTab === 'notes' && (
          <div className="space-y-4" data-testid="apt-notes-tab">
            {/* Add Note Form */}
            <form onSubmit={handleAddNote} className="bg-white border border-zinc-200" data-testid="add-note-form">
              <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
                <NotePencil size={16} weight="fill" className="text-zinc-600" />
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'var(--font-heading)' }}>{t('addNote')}</h3>
              </div>
              <div className="p-4">
                <Textarea
                  value={newNoteText}
                  onChange={e => setNewNoteText(e.target.value)}
                  className="rounded-none text-sm min-h-[80px]"
                  placeholder={t('noteTextPlaceholder')}
                  data-testid="new-note-input"
                />
                <div className="flex justify-end mt-2">
                  <Button type="submit" className="bg-zinc-950 rounded-none" disabled={!newNoteText.trim()} data-testid="save-note-btn">
                    <Plus size={14} className="mr-1" />{t('addNote')}
                  </Button>
                </div>
              </div>
            </form>

            {/* Notes List */}
            {aptNotes.length === 0 ? (
              <div className="bg-white border border-zinc-200 p-8 text-center text-sm text-zinc-400" data-testid="no-notes">{t('noNotes')}</div>
            ) : (
              <div className="space-y-2" data-testid="notes-list">
                {aptNotes.map(note => (
                  <div key={note.note_id} className="bg-white border border-zinc-200" data-testid={`note-${note.note_id}`}>
                    <div className="px-4 py-3">
                      {editingNoteId === note.note_id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editNoteText}
                            onChange={e => setEditNoteText(e.target.value)}
                            className="rounded-none text-sm min-h-[60px]"
                            data-testid={`edit-note-input-${note.note_id}`}
                          />
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" className="rounded-none text-xs" onClick={() => setEditingNoteId(null)}>{t('cancel')}</Button>
                            <Button size="sm" className="bg-zinc-950 rounded-none text-xs" onClick={() => handleUpdateNote(note.note_id)} data-testid={`confirm-edit-note-${note.note_id}`}>{t('save')}</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-zinc-800 whitespace-pre-wrap" data-testid={`note-text-${note.note_id}`}>{note.text}</p>
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-100">
                            <div className="flex items-center gap-3 text-[10px] text-zinc-400">
                              <span className="font-mono">{formatDateDMY(note.created_at?.split('T')[0])}</span>
                              {note.created_by && <span>{note.created_by}</span>}
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => { setEditingNoteId(note.note_id); setEditNoteText(note.text); }} className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-700" title={t('edit')} data-testid={`edit-note-${note.note_id}`}><PencilSimple size={13} /></button>
                              {isAdmin && <button onClick={() => handleDeleteNote(note.note_id)} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-500" title={t('delete')} data-testid={`delete-note-${note.note_id}`}><Trash size={13} /></button>}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* ====== PAYMENT DIALOG (Phase 276 — shared component) ====== */}
        <RecordPaymentDialog
          open={payDialogOpen}
          onOpenChange={setPayDialogOpen}
          buildingId={apartment.building_id}
          apartmentId={apartmentId}
          apartmentNumber={apartment.apartment_number}
          onSaved={fetchAll}
        />

        {/* ====== CREDIT NOTE DIALOG ====== */}
        <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('createCreditNote')} — #{apartment.apartment_number}</DialogTitle></DialogHeader>
            <form onSubmit={handleCreditSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('date')}</Label><DatePickerDMY value={creditForm.date} onChange={v => setCreditForm({ ...creditForm, date: v })} className="mt-1 font-mono" required data-testid="credit-date" /></div>
                <div><Label>{t('amount')} (&euro;)</Label><Input type="number" step="0.01" min="0.01" value={creditForm.amount} onChange={(e) => setCreditForm({ ...creditForm, amount: e.target.value })} className="mt-1 rounded-none font-mono" required data-testid="credit-amount" /></div>
              </div>
              <div><Label>{t('notes')} <span className="text-zinc-400 text-[10px]">({t('optional')})</span></Label><Input value={creditForm.description} onChange={(e) => setCreditForm({ ...creditForm, description: e.target.value })} className="mt-1 rounded-none" placeholder={t('creditNotePlaceholder')} data-testid="credit-description" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreditDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-900 rounded-none" data-testid="credit-submit">{t('createCreditNote')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== EDIT DIALOG — Phase 652: now uses the shared ApartmentFormBody
            component so Add (BuildingDetails) and Edit render IDENTICAL UI.
            Only the dialog chrome + state wiring lives here.
            ====== */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="rounded-none p-0 flex flex-col w-[95vw] max-w-[1400px] max-h-[90vh] sm:!max-w-[1400px]" data-testid="edit-apt-dialog">
            <DialogHeader className="px-5 py-3 border-b border-zinc-200 shrink-0 bg-zinc-50/60">
              <div className="flex items-baseline justify-between gap-4">
                <DialogTitle style={{ fontFamily: 'var(--font-heading)' }} className="text-base font-bold tracking-tight">{t('editApartment')}</DialogTitle>
                {apartment && (
                  <p className="text-[11px] text-zinc-500 font-mono">
                    {apartment.apartment_number ? `#${apartment.apartment_number}` : ''}{buildingSettings?.name ? ` · ${buildingSettings.name}` : ''}
                  </p>
                )}
              </div>
            </DialogHeader>
            <form onSubmit={handleSaveEdit} className="flex-1 flex flex-col min-h-0">
              <div className="flex-1 overflow-y-auto px-5 py-4 bg-zinc-50/30">
                <ApartmentFormBody
                  form={editForm}
                  setForm={setEditForm}
                  plansList={plansList}
                  t={t}
                  apartmentIdHint={apartment?.apartment_id}
                />
              </div>
              {/* Sticky footer */}
              <div className="border-t border-zinc-200 bg-white px-5 py-2.5 flex items-center justify-end gap-2 shrink-0">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-none h-8 text-xs px-3">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none h-8 text-xs px-4" data-testid="save-apt-btn">{t('save')}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== INVOICE CREATE/EDIT DIALOG ====== */}
        <Dialog open={invDialogOpen} onOpenChange={setInvDialogOpen}>
          <DialogContent className="sm:max-w-md rounded-none">
            <DialogHeader><DialogTitle>{editingInvoice ? t('editInvoice') : t('createInvoice')}</DialogTitle></DialogHeader>
            <form onSubmit={handleInvoiceSubmit} className="space-y-4">
              {/* Phase 238 — Manual Invoice Number (optional → auto-generated when blank). */}
              <div>
                <Label>{t('invoiceNumber') || 'Invoice Number'}{' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span></Label>
                <Input
                  value={invForm.invoice_number}
                  onChange={e => setInvForm({ ...invForm, invoice_number: e.target.value })}
                  className="rounded-none mt-1 font-mono"
                  placeholder={t('invoiceNumberPlaceholder') || 'Leave empty to auto-generate (e.g. ODY-0016)'}
                  data-testid="inv-number-input"
                  maxLength={64}
                />
              </div>
              {/* Phase 238 — Full Invoice Date replacing month-only Period. */}
              <div>
                <Label>{t('invoiceDate') || 'Invoice Date'} *</Label>
                <Input
                  type="date"
                  value={invForm.invoice_date}
                  onChange={e => {
                    const newDate = e.target.value;
                    // Phase 802 — When the Invoice Date changes, auto-fill
                    // Period / Start Period / End Period to the new YYYY-MM
                    // for every field the user hasn't manually overridden.
                    // Override detection: a field has been overridden if
                    // its current value differs from the previously-auto
                    // YYYY-MM derived from the OLD invoice_date. Empty
                    // fields are always considered auto (safe to fill).
                    setInvForm(prev => {
                      const prevAuto = prev.invoice_date ? prev.invoice_date.slice(0, 7) : '';
                      const newAuto = newDate ? newDate.slice(0, 7) : '';
                      const isAutoOrEmpty = (val) => !val || val === prevAuto;
                      return {
                        ...prev,
                        invoice_date: newDate,
                        period:       isAutoOrEmpty(prev.period)       ? newAuto : prev.period,
                        start_period: isAutoOrEmpty(prev.start_period) ? newAuto : prev.start_period,
                        end_period:   isAutoOrEmpty(prev.end_period)   ? newAuto : prev.end_period,
                      };
                    });
                  }}
                  required
                  className="rounded-none mt-1 font-mono"
                  data-testid="inv-date-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between gap-2">
                  <Label>{t('period') || 'Period'}{' '}<span className="text-zinc-400 text-xs font-normal">({t('optional') || 'optional'})</span></Label>
                  {/* Phase 244 — Toggle to opt-in to a saved period. When OFF
                      the picker is hidden and the invoice is saved without
                      a period; when ON the user picks the reporting month. */}
                  <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer select-none" data-testid="inv-use-period-label">
                    <input
                      type="checkbox"
                      checked={!!invForm.use_period}
                      onChange={e => setInvForm({ ...invForm, use_period: e.target.checked })}
                      className="rounded-none accent-zinc-950"
                      data-testid="inv-use-period-toggle"
                    />
                    <span>{t('usePeriod') || 'Use Period'}</span>
                  </label>
                </div>
                {invForm.use_period && (
                  <>
                    <Input
                      type="month"
                      value={invForm.period}
                      onChange={e => {
                        // Phase 735.61 — keep start_period synced to the
                        // classic Period picker so users who ignore the
                        // range fields still get the single-month behavior.
                        const v = e.target.value;
                        // Phase 985/987 — Smart owner auto-selection is
                        // now driven by the classic Period picker (multi-
                        // month allocation UI removed). Whenever the user
                        // changes the reporting period we pick the owner
                        // whose ownership window overlaps that month; the
                        // dropdown remains manually editable afterwards.
                        const suggestedOh = _getOwnerHistoryIdForPeriod(v);
                        const next = {
                          ...invForm,
                          period: v,
                          start_period: v,
                          end_period: v,
                          // Force single-month behavior on submit — no
                          // multi-month allocation UI is exposed.
                          allocation_mode: 'equal',
                          manual_allocations: [],
                        };
                        if (suggestedOh) next.owner_history_id = suggestedOh;
                        setInvForm(next);
                      }}
                      className="rounded-none mt-1"
                      data-testid="inv-period-input"
                    />
                    <p className="text-[10px] text-zinc-500 mt-1">{t('periodAutoHint') || 'Auto-filled from Invoice Date — edit if you need a different reporting period.'}</p>
                  </>
                )}
              </div>
              {/* Phase 985 — Invoice Owner selector. Shows ALL historical
                  owners of this apartment (any count). Defaults to the
                  owner matching the current Invoice Period (or the
                  active owner). Auto-updates when the Period changes, but
                  users can override manually at any time. The chosen
                  owner_history_id and an immutable owner-name snapshot
                  are stamped on the invoice at save time. */}
              {ownerHistoryList.length > 0 && (
                <div data-testid="inv-owner-field">
                  <Label>{t('invoiceOwner') || 'Invoice Owner'}</Label>
                  <select
                    value={invForm.owner_history_id || ''}
                    onChange={e => setInvForm({ ...invForm, owner_history_id: e.target.value })}
                    className="w-full rounded-none mt-1 border border-zinc-300 bg-white px-3 py-2 text-sm font-mono"
                    data-testid="inv-owner-select"
                  >
                    <option value="">{t('none') || '— None —'}</option>
                    {ownerHistoryList.map(oh => {
                      const name = [oh.first_name, oh.last_name].filter(Boolean).join(' ') || oh.owner_name || '—';
                      const period = oh.start_date
                        ? `${formatDateDMY(oh.start_date)}${oh.end_date ? ' – ' + formatDateDMY(oh.end_date) : ' – ' + (t('present') || 'present')}`
                        : '';
                      return (
                        <option key={oh.owner_history_id} value={oh.owner_history_id}>
                          {name}{period ? ` (${period})` : ''}{oh.status === 'active' ? ' • ' + (t('active') || 'active') : ''}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-[10px] text-zinc-500 mt-1">
                    {t('invoiceOwnerHint') || 'Auto-selected from the invoice period. Change if this invoice belongs to a different historical owner. The selection is permanently linked to this invoice.'}
                  </p>
                </div>
              )}
              <div><Label>{t('amount')} *</Label><Input type="number" step="0.01" min="0.01" value={invForm.amount} onChange={e => setInvForm({ ...invForm, amount: e.target.value })} required className="rounded-none mt-1" data-testid="inv-amount-input" /></div>
              {/* Phase 1002r — Reserve Fund auto-add preview. Shown for
                  Common Expenses invoices (default category) when the
                  invoice period falls inside an active Extra Monthly
                  Reserve schedule. The backend appends the second line
                  automatically on Save; this banner previews it. */}
              {!editingInvoice && effectiveReserve && effectiveReserve.active && (effectiveReserve.per_apartment_amount || 0) > 0 && (
                <div className="border border-emerald-200 bg-emerald-50/60 px-3 py-2 flex items-start gap-3" data-testid="inv-extra-reserve-preview">
                  <div className="mt-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <div className="flex-1 text-xs">
                    <div className="text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">
                      {t('reserveFundAutoAdded') || 'Reserve Fund will be added automatically'}
                    </div>
                    <div className="text-emerald-900 mt-0.5">
                      + <span className="font-mono font-semibold">&euro;{(effectiveReserve.per_apartment_amount || 0).toFixed(2)}</span>{' '}
                      {t('reserveFund') || 'Reserve Fund'} &nbsp;→&nbsp; {t('newTotal') || 'new total'}:{' '}
                      <span className="font-mono font-semibold">&euro;{(((parseFloat(invForm.amount) || 0) + (effectiveReserve.per_apartment_amount || 0))).toFixed(2)}</span>
                    </div>
                    <div className="text-[10px] text-emerald-700/80 mt-0.5">
                      {t('effectiveFrom') || 'Effective from'} {(effectiveReserve.start_date || '').split('-').reverse().join('/')}
                      {effectiveReserve.end_date ? ` ${t('until') || 'until'} ${(effectiveReserve.end_date || '').split('-').reverse().join('/')}` : ` (${t('openEnded') || 'open-ended'})`}
                    </div>
                  </div>
                </div>
              )}
              <div><Label>{t('notes')}</Label><Input value={invForm.notes} onChange={e => setInvForm({ ...invForm, notes: e.target.value })} className="rounded-none mt-1" data-testid="inv-notes-input" /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setInvDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="inv-submit-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== DELETE PAYMENT DIALOG ====== */}
        <AlertDialog open={deletePayDialogOpen} onOpenChange={setDeletePayDialogOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment') || t('confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDeletePayment')}
                {deletingPay?.receipt_number && (
                  <span className="block mt-2 text-xs text-zinc-500">
                    {t('receiptNumber')}: <span className="font-mono font-semibold">{deletingPay.receipt_number}</span>
                  </span>
                )}
                {deletingPay && (
                  <span className="block mt-1 text-xs text-zinc-500">
                    {t('amount')}: <span className="font-mono font-semibold">&euro;{Math.abs(deletingPay.amount || 0).toFixed(2)}</span>
                  </span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="cancel-delete-pay">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700 rounded-none"
                data-testid="confirm-delete-pay"
                onClick={async () => {
                  if (!deletingPay) return;
                  try {
                    await axios.delete(`${API}/apartment-payments/${deletingPay.payment_id}`);
                    toast.success(t('paymentDeleted'));
                    setDeletePayDialogOpen(false);
                    setDeletingPay(null);
                    // Phase 1010 — broadcast so Apartments list refreshes
                    try {
                      window.dispatchEvent(new CustomEvent('cleanpro:apartment-payment-deleted', {
                        detail: { building_id: apartment?.building_id, apartment_id: apartmentId, saved_at: new Date().toISOString() },
                      }));
                    } catch { /* non-fatal */ }
                    fetchAll();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || t('errorOccurred'));
                  }
                }}
              >{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== DELETE INVOICE DIALOG ====== */}
        <AlertDialog open={deleteInvDialogOpen} onOpenChange={setDeleteInvDialogOpen}>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('confirmDelete')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('deleteInvoiceWarning')}
                {deletingInvoice && (deletingInvoice.paid_amount || 0) > 0 && (
                  <span className="block mt-2 text-amber-600 font-semibold">{t('invoiceHasPayments')}: &euro;{(deletingInvoice.paid_amount || 0).toFixed(2)}</span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600 hover:bg-red-700 rounded-none" data-testid="confirm-delete-invoice">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ====== RECURRING RULE DIALOG ====== */}
        <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
          <DialogContent className="sm:max-w-lg rounded-none">
            <DialogHeader><DialogTitle>{editingRule ? t('editRule') : t('addRule')}</DialogTitle></DialogHeader>
            <form onSubmit={handleRuleSubmit} className="space-y-4">
              {!editingRule && (
                <div>
                  <Label>{t('scope')}</Label>
                  <Select value={ruleForm.scope} onValueChange={v => setRuleForm({ ...ruleForm, scope: v })}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="rule-scope"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="apartment">{t('thisApartment')}</SelectItem>
                      <SelectItem value="building">{t('allApartments')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('dayOfMonth')} (1-31) *</Label><Input type="number" min="1" max="31" value={ruleForm.day_of_month} onChange={e => setRuleForm({ ...ruleForm, day_of_month: e.target.value })} required className="rounded-none mt-1" data-testid="rule-day" /><p className="text-[10px] text-zinc-400 mt-1">{t('dayOfMonthShortMonthHint') || 'For 29/30/31: shorter months (e.g. Feb) auto-run on the last calendar day.'}</p></div>
                <div><Label>{t('amount')} ({t('optional')})</Label><Input type="number" step="0.01" value={ruleForm.amount} onChange={e => setRuleForm({ ...ruleForm, amount: e.target.value })} placeholder={t('useMonthlyFee')} className="rounded-none mt-1" data-testid="rule-amount" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>{t('startDate')} *</Label><DatePickerDMY value={ruleForm.start_date} onChange={v => setRuleForm({ ...ruleForm, start_date: v })} required className="mt-1" data-testid="rule-start" /></div>
                <div><Label>{t('endDate')} ({t('optional')})</Label><DatePickerDMY value={ruleForm.end_date} onChange={v => setRuleForm({ ...ruleForm, end_date: v })} className="mt-1" data-testid="rule-end" /></div>
              </div>
              <div className="flex items-center gap-3 py-2">
                <button type="button" onClick={() => setRuleForm({ ...ruleForm, send_email: !ruleForm.send_email })} className="flex items-center gap-2" data-testid="rule-email-toggle">
                  {ruleForm.send_email ? <ToggleRight size={24} weight="fill" className="text-emerald-600" /> : <ToggleLeft size={24} weight="fill" className="text-zinc-400" />}
                </button>
                <div className="flex-1">
                  <Label className="cursor-pointer block" onClick={() => setRuleForm({ ...ruleForm, send_email: !ruleForm.send_email })}>{t('autoEmailInvoices')}</Label>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{t('autoEmailInvoicesHint')}</p>
                </div>
              </div>
              {/* Phase 1013 — Send invoice to (recipient selector). Visible
                  only for apartment-scope rules (building-scope rules
                  auto-resolve each apartment's primary email at send time). */}
              {ruleForm.scope === 'apartment' && ruleForm.send_email && (() => {
                const opts = recipientOptionsForApartment();
                if (opts.length === 0) {
                  return (
                    <div className="p-3 bg-amber-50 border border-amber-200" data-testid="rule-no-emails-warning">
                      <p className="text-xs font-semibold text-amber-800">{t('noEmailsConfigured')}</p>
                      <p className="text-[11px] text-amber-700 mt-1">{t('noEmailsConfiguredHint')}</p>
                    </div>
                  );
                }
                return (
                  <div>
                    <Label>{t('sendInvoiceTo')} *</Label>
                    <Select value={ruleForm.recipient_email || (opts.length === 1 ? opts[0].email : '')} onValueChange={v => setRuleForm({ ...ruleForm, recipient_email: v })}>
                      <SelectTrigger className="mt-1 rounded-none" data-testid="rule-recipient-select"><SelectValue placeholder={t('autoUsePrimary')} /></SelectTrigger>
                      <SelectContent>
                        {opts.map(opt => (
                          <SelectItem key={opt.email} value={opt.email} data-testid={`rule-recipient-opt-${opt.email}`}>
                            <span className="font-mono text-xs">{opt.email}</span>
                            <span className="ml-2 text-[10px] text-zinc-500">{opt.label}{opt.name ? ` · ${opt.name}` : ''}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })()}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setRuleDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="rule-submit-btn">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== CREDIT ALLOCATION DIALOG ====== */}
        <Dialog open={creditAllocDialogOpen} onOpenChange={setCreditAllocDialogOpen}>
          <DialogContent className="sm:max-w-lg rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('applyCreditToInvoices')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleApplyCreditManual} className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200">
                <span className="text-sm font-semibold text-purple-800">{t('availableCredit')}</span>
                <span className="text-lg font-bold font-mono text-purple-700">&euro;{availableCredit.toFixed(2)}</span>
              </div>

              {creditAllocInvoices.length === 0 ? (
                <p className="text-sm text-zinc-500 text-center py-4">{t('noUnpaidInvoices')}</p>
              ) : (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">{t('unpaidInvoices')}</p>
                  {creditAllocInvoices.map(inv => (
                    <div key={inv.invoice_id} className="flex items-center gap-3 p-2 border border-zinc-200 hover:bg-zinc-50">
                      <div className="flex-1">
                        <span className="text-sm font-mono font-semibold text-zinc-700">{inv.invoice_number || '-'}</span>
                        <span className="text-xs text-zinc-400 ml-2">{inv.period}</span>
                        <span className="text-xs text-red-600 ml-2">{t('remaining')}: &euro;{(inv.remaining || 0).toFixed(2)}</span>
                      </div>
                      <Input type="number" step="0.01" min="0" max={Math.min(availableCredit, inv.remaining || 0)}
                        value={creditAllocAmounts[inv.invoice_id] || ''}
                        onChange={e => setCreditAllocAmounts(prev => ({ ...prev, [inv.invoice_id]: e.target.value }))}
                        placeholder="0.00" className="w-28 rounded-none font-mono h-8 text-sm"
                        data-testid={`credit-alloc-${inv.invoice_id}`} />
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between items-center pt-2 border-t border-zinc-200">
                <span className="text-sm font-semibold">{t('totalToApply')}</span>
                <span className={`font-mono font-bold ${creditAllocTotal <= availableCredit ? 'text-emerald-700' : 'text-red-700'}`}>
                  &euro;{creditAllocTotal.toFixed(2)} / &euro;{availableCredit.toFixed(2)}
                </span>
              </div>
              {creditAllocTotal > availableCredit + 0.01 && (
                <p className="text-xs text-red-600">{t('exceedsCredit')}</p>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreditAllocDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-purple-700 hover:bg-purple-800 rounded-none" disabled={creditAllocTotal <= 0 || creditAllocTotal > availableCredit + 0.01} data-testid="apply-credit-submit">{t('applyCredit')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== PHASE 302 — ALLOCATE OPENING-BALANCE CREDIT ====== */}
        {/* Opens from the Opening Balance card when the apartment carries
            a NEGATIVE OB with remaining credit. The user picks which
            currently unpaid invoices to settle, by how much. Each row is
            capped at min(requested, invoice remaining, credit remaining)
            on the server (`POST /apartments/{id}/allocate-credit`). */}
        <AllocateCreditDialog
          open={allocateCreditOpen}
          onOpenChange={setAllocateCreditOpen}
          apartment={apartment}
          apartmentId={apartmentId}
          unpaidInvoices={allocCreditInvoices}
          setUnpaidInvoices={setAllocCreditInvoices}
          amounts={allocCreditAmounts}
          setAmounts={setAllocCreditAmounts}
          loading={allocCreditLoading}
          setLoading={setAllocCreditLoading}
          onAllocated={async () => {
            await fetchAll();
            toast.success('Credit allocated.');
            setAllocateCreditOpen(false);
          }}
          t={t}
        />

        {/* Phase 1003 — Credit Allocations viewer & editor */}
        <CreditAllocationsDialog
          open={viewAllocationsOpen}
          onOpenChange={setViewAllocationsOpen}
          apartmentId={apartmentId}
          onSaved={async () => { await fetchAll(); }}
          t={t}
        />

        {/* ====== STATEMENT OPENING BALANCE DIALOG ====== */}
        <Dialog open={stmtObDialogOpen} onOpenChange={setStmtObDialogOpen}>          <DialogContent className="sm:max-w-sm rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('setOpeningBalance')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveStmtOb} className="space-y-4">
              <div>
                <Label>{t('amount')} (&euro;)</Label>
                <Input type="number" step="0.01" value={stmtObAmount} onChange={e => setStmtObAmount(e.target.value)} required className="mt-1 rounded-none font-mono" data-testid="stmt-ob-amount" />
              </div>
              <div>
                <Label>{t('date')}</Label>
                <DatePickerDMY value={stmtObDate} onChange={setStmtObDate} required className="mt-1 rounded-none font-mono" data-testid="stmt-ob-date" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setStmtObDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-stmt-ob">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* ====== EDIT RECEIPT DIALOG ====== */}
        <Dialog open={editReceiptDialogOpen} onOpenChange={setEditReceiptDialogOpen}>
          <DialogContent className="sm:max-w-6xl rounded-none" data-testid="edit-receipt-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>
                {t('editReceipt')} {editingReceipt?.receipt_number && `— ${editingReceipt.receipt_number}`}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditReceiptSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t('amount')} (&euro;) *</Label>
                  <Input type="number" step="0.01" min="0" value={editReceiptForm.amount}
                    onChange={e => setEditReceiptForm({ ...editReceiptForm, amount: e.target.value })}
                    required className="mt-1 rounded-none font-mono" data-testid="edit-receipt-amount" />
                </div>
                <div>
                  <Label>{t('date')} *</Label>
                  <DatePickerDMY value={editReceiptForm.payment_date} onChange={v => setEditReceiptForm({ ...editReceiptForm, payment_date: v })} required className="mt-1 font-mono" data-testid="edit-receipt-date" />
                </div>
              </div>
              <div>
                <Label>{t('paymentMethod')}</Label>
                <Select value={editReceiptForm.payment_method} onValueChange={v => setEditReceiptForm({ ...editReceiptForm, payment_method: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="edit-receipt-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">{t('cash')}</SelectItem>
                    <SelectItem value="bank">{t('bank')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Phase 241 — Editable receipt number; backend rejects
                  duplicates within the same building (HTTP 409). */}
              <div>
                <Label>{t('receiptNumber')}{' '}<span className="text-zinc-400 text-xs font-normal">({t('optional')})</span></Label>
                <Input
                  value={editReceiptForm.receipt_number}
                  onChange={e => setEditReceiptForm({ ...editReceiptForm, receipt_number: e.target.value })}
                  className="mt-1 rounded-none font-mono"
                  placeholder={t('receiptNumberPlaceholder') || 'Leave empty to auto-generate (e.g. RCP-0017)'}
                  maxLength={64}
                  data-testid="edit-receipt-number"
                />
              </div>
              <div>
                <Label>{t('notes')}</Label>
                <Input value={editReceiptForm.notes} onChange={e => setEditReceiptForm({ ...editReceiptForm, notes: e.target.value })}
                  className="mt-1 rounded-none" data-testid="edit-receipt-notes" />
              </div>
              {ownerHistoryList.length > 0 && (
                <OwnerSelect owners={ownerHistoryList} value={editReceiptForm.owner_history_id}
                  onChange={(v) => setEditReceiptForm({ ...editReceiptForm, owner_history_id: v })} testid="edit-receipt-owner-select"
                  hint={language === 'el' ? 'Αλλάζει μόνο ο ιδιοκτήτης της πληρωμής· ποσά, ημερομηνία και κατανομές παραμένουν ίδια.' : 'Only the payment owner changes; amounts, date and allocations stay the same.'} />
              )}

              {/* Phase 735.57 — Professional Invoice Allocation panel.
                  Replaces the older "row of anonymous numeric inputs" with a
                  Zoho/Xero-style table so the user always knows exactly
                  which invoice a portion of the receipt is being paid
                  against. Backend allocation logic is unchanged — only the
                  presentation of the same `editReceiptAllocations` state.  */}
              {editReceiptInvoices.length > 0 && (
                <div className="border border-zinc-300 bg-white" data-testid="apartment-allocation-panel">
                  {/* Live summary strip */}
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-3 px-3 py-2 border-b border-zinc-200 bg-zinc-50 text-[11px] font-mono">
                    <div>
                      <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{t('rcpNo') || 'Receipt #'}</div>
                      <div className="text-xs font-semibold text-zinc-900">{editReceiptForm.receipt_number || editingReceipt?.receipt_number || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{t('date') || 'Date'}</div>
                      <div className="text-xs font-semibold text-zinc-900">{editReceiptForm.payment_date || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{t('method') || 'Method'}</div>
                      <div className="text-xs font-semibold text-zinc-900 capitalize">{editReceiptForm.payment_method || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{t('amount') || 'Payment'}</div>
                      <div className="text-xs font-semibold text-zinc-900">&euro;{editReceiptAmount.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{t('allocated') || 'Allocated'}</div>
                      <div className={`text-xs font-semibold ${editReceiptTotalAllocated > editReceiptAmount + 0.005 ? 'text-red-700' : 'text-zinc-900'}`} data-testid="apt-alloc-total">&euro;{editReceiptTotalAllocated.toFixed(2)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-zinc-500 tracking-wider">{t('remaining') || 'Remaining'}</div>
                      <div className="text-xs font-semibold text-emerald-700" data-testid="apt-alloc-remaining">&euro;{Math.max(0, editReceiptAmount - editReceiptTotalAllocated).toFixed(2)}</div>
                    </div>
                  </div>

                  {/* Action row */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">
                      {t('invoiceAllocation') || 'Invoice Allocation'}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button type="button" variant="outline" size="sm"
                        onClick={() => {
                          // Auto-allocate oldest first (already sorted by
                          // invoice date on load; opening balance stays at
                          // its usual position).
                          const cleared = {};
                          let left = editReceiptAmount;
                          for (const inv of editReceiptInvoices) {
                            if (left <= 0.005) { cleared[inv.invoice_id] = { amount: '' }; continue; }
                            const rem = Math.max(0, Number(inv.remaining || 0));
                            const give = Math.min(rem, left);
                            cleared[inv.invoice_id] = { amount: give > 0 ? give.toFixed(2) : '' };
                            left = Math.max(0, left - give);
                          }
                          setEditReceiptAllocations(cleared);
                        }}
                        className="rounded-none h-7 text-xs gap-1"
                        data-testid="apt-alloc-auto"
                        disabled={editReceiptAmount <= 0}
                      >
                        {t('autoAllocate') || 'Auto Allocate'}
                      </Button>
                      <Button type="button" variant="outline" size="sm"
                        onClick={() => {
                          const cleared = {};
                          for (const inv of editReceiptInvoices) cleared[inv.invoice_id] = { amount: '' };
                          setEditReceiptAllocations(cleared);
                        }}
                        className="rounded-none h-7 text-xs gap-1"
                        data-testid="apt-alloc-clear"
                      >
                        {t('clearAllocation') || 'Clear'}
                      </Button>
                    </div>
                  </div>

                  {/* Invoice table */}
                  <div className="max-h-[45vh] overflow-y-auto">
                    <table className="w-full text-sm" data-testid="apt-alloc-table">
                      <thead className="sticky top-0 z-10 bg-zinc-100 shadow-[inset_0_-1px_0_0_rgba(0,0,0,0.08)]">
                        <tr className="text-[10px] uppercase tracking-wider text-zinc-600">
                          <th className="text-left px-3 py-2 bg-zinc-100">{t('invoiceNumber') || 'Invoice #'}</th>
                          <th className="text-left px-3 py-2 bg-zinc-100">{t('date') || 'Date'}</th>
                          <th className="text-left px-3 py-2 bg-zinc-100">{t('dueDate') || 'Due Date'}</th>
                          <th className="text-left px-3 py-2 bg-zinc-100">{t('period') || 'Period / Description'}</th>
                          <th className="text-right px-3 py-2 bg-zinc-100">{t('amount') || 'Amount'}</th>
                          <th className="text-right px-3 py-2 bg-zinc-100">{t('paid') || 'Paid'}</th>
                          <th className="text-right px-3 py-2 bg-zinc-100">{t('balance') || 'Balance'}</th>
                          <th className="text-left px-3 py-2 bg-zinc-100">{t('status') || 'Status'}</th>
                          <th className="text-right px-3 py-2 bg-zinc-100 w-32">{t('allocate') || 'Allocate'}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editReceiptInvoices.map((inv, idx) => {
                          const isOpening = !!inv.is_opening_balance;
                          const alloc = editReceiptAllocations[inv.invoice_id]?.amount || '';
                          const amount = Number(inv.amount || 0);
                          const paid = Number(inv.paid_amount || 0);
                          const balance = Number(inv.remaining || 0);
                          const rowBg = isOpening
                            ? 'bg-amber-50/60'
                            : (idx % 2 === 0 ? 'bg-white' : 'bg-zinc-50/60');
                          const period = isOpening
                            ? (inv.opening_balance_notes
                                ? `${t('openingBalance') || 'Opening Balance'} – ${inv.opening_balance_notes}`
                                : (t('openingBalance') || 'Opening Balance'))
                            : (inv.period || '—');
                          return (
                            <tr key={inv.invoice_id}
                                className={`border-b border-zinc-100 hover:bg-zinc-100/60 ${rowBg}`}
                                data-testid={`apt-alloc-row-${inv.invoice_id}`}>
                              <td className="px-3 py-2 font-mono text-xs">
                                {isOpening
                                  ? <span className="text-amber-800 font-bold text-[10px] uppercase tracking-wider">OPENING</span>
                                  : (inv.invoice_number || '—')}
                              </td>
                              <td className="px-3 py-2 font-mono text-xs text-zinc-600">{inv.invoice_date || inv.date || '—'}</td>
                              <td className="px-3 py-2 font-mono text-xs text-zinc-600">{inv.due_date || '—'}</td>
                              <td className="px-3 py-2 text-xs text-zinc-700 max-w-[240px] truncate" title={period}>{period}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs">&euro;{amount.toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs text-emerald-700">&euro;{paid.toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-mono text-xs font-semibold text-red-700">&euro;{balance.toFixed(2)}</td>
                              <td className="px-3 py-2">
                                {isOpening ? (
                                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-200 bg-amber-50 text-amber-700">carry</span>
                                ) : (
                                  <InvoiceStatusBadge amount={amount} paidAmount={paid} testId={`apt-alloc-status-${inv.invoice_id}`} />
                                )}
                              </td>
                              <td className="px-3 py-2 text-right">
                                <Input
                                  type="number" step="0.01" min="0"
                                  value={alloc}
                                  onChange={(e) => {
                                    let v = e.target.value;
                                    // Clamp on input to keep users honest:
                                    // (1) never over the invoice balance,
                                    // (2) never over the payment amount minus
                                    // the sum of the other allocations.
                                    const num = parseFloat(v);
                                    if (!Number.isNaN(num)) {
                                      const otherAlloc = Object.entries(editReceiptAllocations).reduce((s, [k, a]) => k === inv.invoice_id ? s : s + (parseFloat(a?.amount) || 0), 0);
                                      const paymentRoom = Math.max(0, editReceiptAmount - otherAlloc);
                                      const cap = Math.min(balance, paymentRoom);
                                      if (num > cap + 0.005) v = cap.toFixed(2);
                                      if (num < 0) v = '0';
                                    }
                                    setEditReceiptAllocations(prev => ({ ...prev, [inv.invoice_id]: { amount: v } }));
                                  }}
                                  placeholder="0.00"
                                  className="rounded-none font-mono h-8 text-right text-xs w-28"
                                  data-testid={`edit-receipt-alloc-${inv.invoice_id}`}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="sticky bottom-0 bg-zinc-100">
                        <tr className="border-t-2 border-zinc-300 font-semibold">
                          <td colSpan={6} className="px-3 py-2 text-[11px] uppercase tracking-wider text-zinc-700 bg-zinc-100">
                            {t('totals') || 'Totals'}
                            <span className="ml-1 text-zinc-400 font-normal">({editReceiptInvoices.length} {editReceiptInvoices.length === 1 ? (t('invoice') || 'invoice') : (t('invoices') || 'invoices')})</span>
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-red-800 bg-zinc-100" data-testid="apt-alloc-total-balance">
                            &euro;{editReceiptInvoices.reduce((s, inv) => s + Number(inv.remaining || 0), 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 bg-zinc-100"></td>
                          <td className="px-3 py-2 text-right font-mono text-xs bg-zinc-100" data-testid="apt-alloc-total-allocated">
                            &euro;{editReceiptTotalAllocated.toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  {editReceiptAmount - editReceiptTotalAllocated > 0.01 && (
                    <p className="px-3 py-2 text-xs text-purple-700 bg-purple-50/60 border-t border-zinc-200">
                      {t('overpayment') || 'Overpayment'}: &euro;{(editReceiptAmount - editReceiptTotalAllocated).toFixed(2)} → {t('creditBalance') || 'Credit Balance'}
                    </p>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditReceiptDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
                <Button type="submit" className="bg-zinc-950 rounded-none" data-testid="save-edit-receipt">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <InvoicePaymentsDialog apartmentId={apartmentId} invoice={payInvoice} onClose={() => setPayInvoice(null)} />
        <PaymentEmailHistoryDialog payment={emailHistPay} onClose={() => setEmailHistPay(null)} onSend={sendReceiptEmail} />
        <SendEmailDialog
          open={emailDialogOpen}
          onOpenChange={setEmailDialogOpen}
          pdfUrl={emailProps.pdfUrl}
          filename={emailProps.filename}
          defaultEmail={emailProps.defaultEmail}
          templateType={emailProps.templateType}
          dynamicFields={emailProps.dynamicFields}
          apartmentId={apartmentId}
          buildingId={apartment?.building_id || ''}
          docType={emailProps.templateType}
          relatedId={emailProps.relatedId}
          onSent={fetchEmailHistory}
          apartmentRecipients={apartment ? {
            primary: apartment.primary_email || apartment.email || apartment.tenant_email || '',
            owner_email: apartment.email || '',
            tenant_email: apartment.tenant_email || '',
            extra_emails: apartment.extra_emails || [],
            responsible_role: apartment.payment_responsibility || 'owner',
            responsible_name: (apartment.payment_responsibility === 'tenant' ? apartment.tenant_name : apartment.owner_name) || '',
          } : null}
        />

        {/* Edit Payment Dialog */}
        <Dialog open={editPayDialogOpen} onOpenChange={setEditPayDialogOpen}>
          <DialogContent className="sm:max-w-sm rounded-none">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>{t('editPayment')}</DialogTitle>
              <DialogDescription className="sr-only">{t('editPayment')}</DialogDescription>
            </DialogHeader>
            {editingPayment && (
              <div className="space-y-3">
                <div className="bg-zinc-50 border border-zinc-200 p-2 text-xs font-mono">
                  {t('apartment')}: {apartment?.apartment_number || ''} — {editingPayment.date}
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('amount')} (&euro;)</Label>
                  <Input type="number" step="0.01" value={editPayForm.amount} onChange={e => setEditPayForm({ ...editPayForm, amount: parseFloat(e.target.value) || 0 })} className="rounded-none mt-1 font-mono" data-testid="edit-pay-amount" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('date')}</Label>
                  <Input type="date" value={editPayForm.date} onChange={e => setEditPayForm({ ...editPayForm, date: e.target.value })} className="rounded-none mt-1 font-mono" data-testid="edit-pay-date" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('notes')}</Label>
                  <Input value={editPayForm.notes} onChange={e => setEditPayForm({ ...editPayForm, notes: e.target.value })} className="rounded-none mt-1" data-testid="edit-pay-notes" />
                </div>
                {ownerHistoryList.length > 0 && (
                  <OwnerSelect owners={ownerHistoryList} value={editPayForm.owner_history_id}
                    onChange={(v) => setEditPayForm({ ...editPayForm, owner_history_id: v })} testid="edit-pay-owner-select" />
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditPayDialogOpen(false)} className="rounded-none">{t('cancel')}</Button>
              <Button onClick={handleEditPayment} className="bg-zinc-950 rounded-none" data-testid="confirm-edit-pay-btn">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
